import React, { useEffect, useState, useCallback, useRef } from 'react';
import PageHead from './PageHead';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy, faDumbbell, faFire, faStar, faArrowLeft, faArrowRight, faBullseye, faMobileScreen } from '@fortawesome/free-solid-svg-icons';
import Game from './Game';
import LoginModal from './LoginModal';
import HintUnlockModal from './HintUnlockModal';
import PurchaseModal from './PurchaseModal';
import StreakMilestoneModal from './StreakMilestoneModal';
import StreakRecoveryModal from './StreakRecoveryModal';
import WrongWayModal from './WrongWayModal';
import FeedbackPrompt from './FeedbackPrompt';
import RankProgressBar from './RankProgressBar';
import RankUpCelebration from './RankUpCelebration';
import RankProgressionModal from './RankProgressionModal';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage, useTranslation } from '../contexts/LanguageContext';
import { useMonetization } from '../contexts/MonetizationContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { findSmartHint, buildHintInstruction, buildHintClueLines, pickClueDefinition, isSameHint, HINT_PENALTY_CLUE } from '../utils/hintSolver';
import { LANGUAGE_BY_CODE } from '../i18n/languages';
import { enqueueSubmit, flushSubmitOutbox, isPermanentRejection } from '../utils/submitOutbox';
import { trackEvent } from '../utils/analytics';
import { canRequestStoreReview, requestStoreReview, storeUrlForThisDevice } from '../services/storeReview';
import { isNative } from '../utils/native';
import useFitLabels from '../hooks/useFitLabels';
import { upperWord, foldLetters } from '../utils/letters';
import '../css/GamePage.css';

// Phone browsers see the store link on the ladder-complete panel at most once a month.
const APP_NUDGE_KEY = 'wl_app_nudge_dismissed_at';
const APP_NUDGE_SNOOZE_MS = 30 * 24 * 3600 * 1000;

const OUTBOX_SUBMITTERS = { ladder: api.submitLadderGame, challenge: api.submitChallengeGame };

// Welcome-screen ladder per language: [word, highlighted letter index].
const ONBOARDING_DEMO = {
    en: [['CAT', 2], ['COT', 1], ['COG', 2]],
    hr: [['KOD', 2], ['KAD', 1], ['KAP', 2]],
    de: [['ARM', 0], ['ART', 2], ['ORT', 0]],
    es: [['VOZ', 1], ['VEZ', 1], ['PEZ', 0]],
    fr: [['FIL', 0], ['FIN', 2], ['VIN', 0]],
    id: [['ADA', 2], ['APA', 1], ['API', 2]],
    it: [['ZIO', 0], ['ZIA', 2], ['VIA', 0]],
    pl: [['TOM', 0], ['DOM', 0], ['DYM', 1]],
    ru: [['СОК', 2], ['СОН', 2], ['СЫН', 1]],
    pt: [['LUZ', 0], ['LUA', 2], ['RUA', 0]],
    tr: [['KAR', 2], ['KAN', 2], ['TAN', 0]],
};

// A just-opened board (no guesses, under a second) has nothing to resume; saving it as the
// next puzzle opens hit "No active progress" 404s before its row existed.
const isFreshBoard = (state) => state.previousGuesses.length === 0 && state.nextGuesses.length === 0
    && state.elapsedSeconds < 1;

function GamePage() {
    const { user, isAuthenticated, promptLogin, showLoginPrompt, dismissLoginPrompt, loginPromptMessage, loginPromptSignup } = useAuth();
    const { language, setLanguage, SUPPORTED_LANGUAGES, LANGUAGE_NAMES } = useLanguage();
    const { t, locale } = useTranslation();
    const {
        hintBalance,
        freeHintsRemaining,
        canUseFreeHint,
        hasAdHintAvailable,
        useHint: checkAndUseHint,
        refreshHintStatus,
        resetGameHintStatus,
        openHintUnlockModal,
        openPurchaseModal,
        purchasesEnabled,
        showHintUnlockModal, // eslint-disable-line no-unused-vars
        refreshAds,
        isNativeMobile,
        streakData,
        refreshStreak,
        recoverStreakWithHints,
        loading: monetizationLoading,
    } = useMonetization();
    const { preferences, updatePreference, loaded: prefsLoaded } = usePreferences();

    const [gameData, setGameData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mode, setMode] = useState(() => {
        // Staff-only marketing-video hook: if the URL carries explicit
        // start_word/target_word params, boot straight into custom mode so
        // the first loadGame fetch is for the requested puzzle. Avoids a
        // race where the initial ranked/practice fetch resolves last and
        // overwrites the custom puzzle. Backend still gates mode=custom
        // behind is_staff, so a non-staff visitor with these params falls
        // back server-side.
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('share')) return 'shared';
        if (urlParams.get('start_word') && urlParams.get('target_word')) {
            return 'custom';
        }
        const saved = localStorage.getItem('gameMode');
        // First-run: a genuinely new visitor (no saved mode, never saw the
        // tutorial, not already onboarded) gets the guided onboarding flow.
        // Every existing/returning player skips it. See docs/ONBOARDING_REDESIGN.md.
        const onbCompleted = localStorage.getItem('wl_onboarding_completed') === 'true';
        const onbStep = localStorage.getItem('wl_onboarding_step');
        const tutorialSeen = localStorage.getItem('wordlabyrinth_tutorial_seen');
        if (!saved && !onbCompleted && onbStep !== 'done' && onbStep !== 'skipped' && !tutorialSeen) {
            return 'onboarding';
        }
        // Map legacy 'random' to 'practice'
        if (saved === 'random') return 'practice';
        return saved && ['ranked', 'practice', 'expert', 'custom', 'challenge'].includes(saved) ? saved : 'ranked';
    });
    const [difficulty, setDifficulty] = useState(() => {
        const saved = localStorage.getItem('gameDifficulty');
        const parsed = parseInt(saved);
        return parsed >= 1 && parsed <= 10 ? parsed : 1;
    });
    const [onboardingStep, setOnboardingStep] = useState(() => {
        // Resume mid-onboarding after a refresh (B8) instead of dropping back to
        // the gate. 0 gate · 1 guided · 2 solo · 3 handoff · 'done'/'skipped'.
        try {
            const s = localStorage.getItem('wl_onboarding_step');
            if (s === 'done' || s === 'skipped') return s;
            const n = parseInt(s, 10);
            if (n === 1 || n === 2 || n === 3) return n;
        } catch { /* ignore */ }
        return 0;
    });
    const [expertMinSteps, setExpertMinSteps] = useState(8);
    const [rankProgress, setRankProgress] = useState(null);
    const [rankProgressUpdate, setRankProgressUpdate] = useState(null);
    const [showRankUpCelebration, setShowRankUpCelebration] = useState(false);
    const [decayUpdate, setDecayUpdate] = useState(null);
    const [gameProgress, setGameProgress] = useState({
        wonGames: 0,
        totalGames: 0,
        levelNumber: 1,
        progress: 0,
        isCompleted: false,
    });
    const [navigation, setNavigation] = useState({
        canGoBack: false,
        canGoNext: false,
        isFirstGame: true,
        isLastGame: false,
        isLastLevel: false,
    });
    const [, setCurrentPath] = useState([]); // Path stored for potential future use
    const [customWords, setCustomWords] = useState(() => {
        // Seed from URL params so the initial loadGame fires for the right
        // puzzle (see the mode initializer above).
        const urlParams = new URLSearchParams(window.location.search);
        const start = upperWord((urlParams.get('start_word') || '').trim());
        const target = upperWord((urlParams.get('target_word') || '').trim());
        return { start, target };
    });
    const [backendConnected, setBackendConnected] = useState(true);
    const [practiceStats, setPracticeStats] = useState(null); // B20: practice PBs
    const [pendingMilestones, setPendingMilestones] = useState(null);
    const [showRecoveryModal, setShowRecoveryModal] = useState(false);
    const [showWrongWayModal, setShowWrongWayModal] = useState(false);
    const [hintEarnedToast, setHintEarnedToast] = useState(null);
    const [hintUsedFlash, setHintUsedFlash] = useState(false); // Brief -1 animation on coin badge

    // Ladder system state
    const [ladderData, setLadderData] = useState(null);
    const [sharedSlug, setSharedSlug] = useState(() => new URLSearchParams(window.location.search).get('share'));
    const [sharedChallenge, setSharedChallenge] = useState(null); // the friend's result for mode 'shared'
    const [sharedOutcome, setSharedOutcome] = useState(null);
    const sharedLanguageSetRef = useRef(false);
    const [freshVisitor] = useState(() => {
        try {
            return !localStorage.getItem('gameMode') && !localStorage.getItem('wordlabyrinth_tutorial_seen')
                && localStorage.getItem('wl_onboarding_completed') !== 'true';
        } catch { return false; }
    });
    const [currentGameIndex, setCurrentGameIndex] = useState(0);
    const [victoryFeedback, setVictoryFeedback] = useState(false);
    const [ladderFeedback, setLadderFeedback] = useState(false);
    const updatePreferenceRef = useRef(updatePreference);
    updatePreferenceRef.current = updatePreference;
    const USE_NEW_LADDER = true; // Feature flag - set to false to use old system

    // Skip state
    const [skipAvailable, setSkipAvailable] = useState(false);
    const [skippedGameIndex, setSkippedGameIndex] = useState(null);

    // Weekly challenge state
    const [challengeData, setChallengeData] = useState(null);

    // Ladder celebration dismiss state
    const [ladderCelebrationDismissed, setLadderCelebrationDismissed] = useState(false);
    const modeSelectorRef = useFitLabels([language]);
    // Weekly challenge progress, peeked when the daily ladder is done so the "what next" offer can show it.
    const [challengePeek, setChallengePeek] = useState(null);
    const [appNudgeDismissed, setAppNudgeDismissed] = useState(() => {
        try { return Date.now() - (parseInt(localStorage.getItem(APP_NUDGE_KEY) || '0', 10) || 0) < APP_NUDGE_SNOOZE_MS; }
        catch { return false; }
    });

    // Game history state (for practice/expert back navigation)
    const [gameHistory, setGameHistory] = useState([]);
    const [historyIndex, setHistoryIndex] = useState(-1); // -1 = current game, 0+ = history
    const [historyPage, setHistoryPage] = useState(1);
    const [historyHasMore, setHistoryHasMore] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [hasPastGames, setHasPastGames] = useState(false);

    // Achievement system state
    const gameStartTimeRef = useRef(null);
    const [newlyEarnedAchievements, setNewlyEarnedAchievements] = useState([]);
    const [ratingUpdate, setRatingUpdate] = useState(null);
    const [showRankPromotion, setShowRankPromotion] = useState(false);
    const [rankPromotionData, setRankPromotionData] = useState(null);

    // Email verification banner (from redirect after clicking verification link)
    const [verificationBanner, setVerificationBanner] = useState(null);

    // Hint system state
    const gameRef = useRef(null);
    const modeSwitchRef = useRef(false);
    const [retryCount, setRetryCount] = useState(0); // Track retries for forcing remount
    const [hintsUsed, setHintsUsed] = useState(0);
    const [hintLoading, setHintLoading] = useState(false);
    const [currentHint, setCurrentHint] = useState(null); // Current hint instruction awaiting action
    const [lastHint, setLastHint] = useState(null); // Last paid hint card, re-opened for free while it still applies
    const [hintHistory, setHintHistory] = useState([]); // List of all hints used
    const [hintHistoryExpanded, setHintHistoryExpanded] = useState(false);
    const [totalPenaltyPercent, setTotalPenaltyPercent] = useState(0);
    const [hintError, setHintError] = useState(null);

    // Game state persistence for resume
    const [savedGameState, setSavedGameState] = useState(null); // {previousGuesses, nextGuesses, elapsedSeconds}
    const [completedUserPath, setCompletedUserPath] = useState(null); // solved path of a reopened practice/expert game
    const saveTimerRef = useRef(null); // Debounce timer for server saves
    const currentGameIdRef = useRef(null); // Track current game ID for save endpoint
    const challengeBannerRef = useRef(null); // Challenge banner scroll animation

    // Helper to get localStorage key for a game's state
    const getGameStateStorageKey = useCallback((gameMode, startWord, targetWord) => {
        return `game_state_${gameMode}_${startWord}_${targetWord}`.toLowerCase();
    }, []);

    // Save game state to localStorage
    const saveGameStateToLocal = useCallback((startWord, targetWord, state) => {
        if (!startWord || !targetWord) return;
        try {
            const key = getGameStateStorageKey(mode, startWord, targetWord);
            localStorage.setItem(key, JSON.stringify({ ...state, timestamp: Date.now() }));
        } catch { /* QuotaExceededError or SecurityError - ignore */ }
    }, [mode, getGameStateStorageKey]);

    // Load game state from localStorage
    const loadGameStateFromLocal = useCallback((gameMode, startWord, targetWord) => {
        const key = getGameStateStorageKey(gameMode, startWord, targetWord);
        const saved = localStorage.getItem(key);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                // Ignore stale saves (older than 24 hours)
                if (parsed.timestamp && Date.now() - parsed.timestamp > 24 * 60 * 60 * 1000) {
                    localStorage.removeItem(key);
                    return null;
                }
                return parsed;
            } catch {
                return null;
            }
        }
        return null;
    }, [getGameStateStorageKey]);

    // Clear game state from localStorage
    const clearGameStateFromLocal = useCallback((startWord, targetWord) => {
        if (!startWord || !targetWord) return;
        // Clear for all modes since the same game might appear in different contexts
        ['ranked', 'practice', 'expert', 'challenge', 'custom', 'shared'].forEach(m => {
            const key = getGameStateStorageKey(m, startWord, targetWord);
            localStorage.removeItem(key);
        });
    }, [getGameStateStorageKey]);

    // Save game state to server (debounced)
    const saveGameStateToServer = useCallback((immediate = false) => {
        const gameId = currentGameIdRef.current;
        if (!gameId || !gameData || !gameRef.current) return;

        const gameState = gameRef.current.getGameState();
        if (!gameState || gameState.gameWon) return;

        const elapsed = gameRef.current.getElapsedSeconds?.() ?? 0;
        const state = {
            previousGuesses: gameState.previousGuesses.map(w => upperWord(w)),
            nextGuesses: gameState.nextGuesses.map(w => upperWord(w)),
            elapsedSeconds: elapsed,
        };
        if (isFreshBoard(state)) return;

        // Always save to localStorage immediately
        saveGameStateToLocal(gameData.startWord, gameData.targetWord, state);

        // Server save: debounced or immediate
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

        const doServerSave = () => {
            api.saveGameState(language, gameId, mode, currentGameIndex, state).catch(() => {});
        };

        if (immediate) {
            doServerSave();
        } else {
            saveTimerRef.current = setTimeout(doServerSave, 3000);
        }
    }, [gameData, language, mode, currentGameIndex, saveGameStateToLocal]);

    // Save via sendBeacon (for page unload)
    const saveGameStateBeacon = useCallback(() => {
        const gameId = currentGameIdRef.current;
        if (!gameId || !gameData || !gameRef.current) return;

        const gameState = gameRef.current.getGameState();
        if (!gameState || gameState.gameWon) return;

        const elapsed = gameRef.current.getElapsedSeconds?.() ?? 0;
        const state = {
            previousGuesses: gameState.previousGuesses.map(w => upperWord(w)),
            nextGuesses: gameState.nextGuesses.map(w => upperWord(w)),
            elapsedSeconds: elapsed,
        };
        if (isFreshBoard(state)) return;

        // Save to localStorage
        saveGameStateToLocal(gameData.startWord, gameData.targetWord, state);
        // Save to server via beacon
        api.sendBeaconSaveState(language, gameId, mode, currentGameIndex, state);
    }, [gameData, language, mode, currentGameIndex, saveGameStateToLocal]);

    // Register beforeunload and visibilitychange handlers
    useEffect(() => {
        const handleBeforeUnload = () => saveGameStateBeacon();
        const handleVisibilityChange = () => {
            if (document.hidden) saveGameStateBeacon();
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            // Also save on unmount (navigating away within the app)
            saveGameStateBeacon();
        };
    }, [saveGameStateBeacon]);

    // Record the start of each ladder/challenge game on the server (games 2+ open without a fetch).
    const startedGamesRef = useRef(new Set());
    useEffect(() => {
        const games = mode === 'ranked' ? ladderData?.games : mode === 'challenge' ? challengeData?.games : null;
        const game = games?.[currentGameIndex];
        if (!game || game.isCompleted || startedGamesRef.current.has(`${mode}:${game.id}`)) return;
        startedGamesRef.current.add(`${mode}:${game.id}`);
        api.startLadderGame(language, mode, currentGameIndex).catch(() => {});
    }, [mode, ladderData, challengeData, currentGameIndex, language]);

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        };
    }, []);

    // Show streak recovery modal when recovery is available (skip if already dismissed for this event)
    useEffect(() => {
        if (!prefsLoaded) return; // Wait for server preferences before deciding
        if (streakData?.recovery?.available && !showRecoveryModal) {
            const dismissedFor = preferences.recoveryDismissedFor;
            const recoveryEvent = streakData.recovery.streakLostAt;
            if (dismissedFor && recoveryEvent && dismissedFor === recoveryEvent) return;
            // Small delay so it doesn't flash immediately on page load
            const timer = setTimeout(() => setShowRecoveryModal(true), 500);
            return () => clearTimeout(timer);
        }
    }, [streakData?.recovery?.available, prefsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-dismiss hint earned toast
    useEffect(() => {
        if (hintEarnedToast) {
            const timer = setTimeout(() => setHintEarnedToast(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [hintEarnedToast]);

    // Helper to get localStorage key for a game's hints
    const getHintStorageKey = useCallback((startWord, targetWord) => {
        return `hints_${startWord}_${targetWord}`.toLowerCase();
    }, []);

    // Save hint state to localStorage
    const saveHintState = useCallback((startWord, targetWord, hintData) => {
        try {
            const key = getHintStorageKey(startWord, targetWord);
            localStorage.setItem(key, JSON.stringify(hintData));
        } catch { /* QuotaExceededError or SecurityError - ignore */ }
    }, [getHintStorageKey]);

    // Load hint state from localStorage
    const loadHintState = useCallback((startWord, targetWord) => {
        const key = getHintStorageKey(startWord, targetWord);
        const saved = localStorage.getItem(key);
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch {
                return null;
            }
        }
        return null;
    }, [getHintStorageKey]);

    // Clear hint state from localStorage
    const clearHintState = useCallback((startWord, targetWord) => {
        const key = getHintStorageKey(startWord, targetWord);
        localStorage.removeItem(key);
    }, [getHintStorageKey]);

    // Persist mode and difficulty to localStorage + server
    useEffect(() => {
        if (mode === 'onboarding' || mode === 'shared') return; // transient modes, never a saved preference
        try { localStorage.setItem('gameMode', mode); } catch { /* ignore */ }
        updatePreference('gameMode', mode);
    }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        try { localStorage.setItem('gameDifficulty', difficulty.toString()); } catch { /* ignore */ }
        updatePreference('gameDifficulty', difficulty);
    }, [difficulty]); // eslint-disable-line react-hooks/exhaustive-deps

    // Returning users bypass onboarding once server preferences load — covers a
    // registered user on a new device (empty localStorage) whose server flag
    // (incl. the step-0 backfill of prior players) says they've already played.
    // Only acts at the gate (step 0) so it never interrupts an in-progress flow.
    useEffect(() => {
        if (mode === 'onboarding' && onboardingStep === 0 && prefsLoaded &&
            (preferences.onboardingCompleted || preferences.tutorialSeen)) {
            setMode('ranked');
        }
    }, [prefsLoaded, preferences.onboardingCompleted, preferences.tutorialSeen, mode, onboardingStep]);

    // Strip the competitive game chrome (mode tabs, rank bar, hint/coins, nav,
    // SEO content) while onboarding so the first run is clean and focused.
    useEffect(() => {
        document.body.classList.toggle('wl-onboarding', mode === 'onboarding');
        return () => document.body.classList.remove('wl-onboarding');
    }, [mode]);

    // Handle email verification redirect (from clicking verification link in email)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const verification = params.get('verification');
        if (verification === 'success') {
            setVerificationBanner({ type: 'success', messageKey: 'play.verification.success' });
            promptLogin(t('play.verification.successPrompt'));
            window.history.replaceState({}, '', window.location.pathname);
        } else if (verification === 'expired') {
            setVerificationBanner({ type: 'error', messageKey: 'play.verification.expired' });
            window.history.replaceState({}, '', window.location.pathname);
        } else if (verification === 'invalid') {
            setVerificationBanner({ type: 'error', messageKey: 'play.verification.invalid' });
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Rank progress is fetched once on mount (see useEffect above)
    // and updated from submission responses for ranked/expert/challenge modes

    // Save hint state to localStorage whenever it changes
    useEffect(() => {
        if (gameData && hintHistory.length > 0) {
            saveHintState(gameData.startWord, gameData.targetWord, {
                hintsUsed,
                hintHistory,
                totalPenaltyPercent,
                currentClue: currentHint?.stage === 'clue' ? currentHint : null,
                lastHint,
            });
        }
    }, [gameData, hintsUsed, hintHistory, totalPenaltyPercent, currentHint, lastHint, saveHintState]);

    // Helper to set hint state from game data
    const setHintStateForGame = useCallback((startWord, targetWord, isCompleted) => {
        const savedHints = loadHintState(startWord, targetWord);
        let openClue = null;
        let paidHint = null;
        if (savedHints && !isCompleted) {
            setHintsUsed(savedHints.hintsUsed || 0);
            setHintHistory(savedHints.hintHistory || []);
            setTotalPenaltyPercent(savedHints.totalPenaltyPercent || 0);
            openClue = savedHints.currentClue || null;
            paidHint = savedHints.lastHint || openClue;
        } else {
            setHintsUsed(0);
            setHintHistory([]);
            setTotalPenaltyPercent(0);
            if (isCompleted) {
                clearHintState(startWord, targetWord);
            }
        }
        setCurrentHint(openClue);
        setLastHint(paidHint);
        setHintError(null);
        resetGameHintStatus();
    }, [loadHintState, clearHintState, resetGameHintStatus]);

    const loadGame = useCallback(async () => {
        setLoading(true);
        setError(null);

        // Onboarding gate (0) and handoff (3) render their own screens — no game to fetch.
        if (mode === 'onboarding' && onboardingStep !== 1 && onboardingStep !== 2) {
            setLoading(false);
            return;
        }

        try {
            // Use new ladder API for ranked mode
            if (USE_NEW_LADDER && mode === 'ranked') {
                const data = await api.fetchLadder(language, 'ranked');

                if (data.success && data.ladder && data.ladder.games.length > 0) {
                    setLadderData(data.ladder);
                    const currentIdx = data.progress.currentGameIndex;
                    setCurrentGameIndex(currentIdx);

                    // Update skip and star goal state
                    setSkipAvailable(data.skipAvailable ?? false);
                    setSkippedGameIndex(data.skippedGameIndex ?? null);
                    if (data.rankProgress) setRankProgress(data.rankProgress);
                    if (data.decayUpdate) setDecayUpdate(data.decayUpdate);

                    const currentGame = data.ladder.games[currentIdx];

                    setGameData({
                        startWord: currentGame.startWord,
                        targetWord: currentGame.targetWord,
                        title: currentGame.title,
                        minSteps: currentGame.minSteps,
                        tier: currentGame.tier,
                        maxStars: currentGame.maxStars ?? 3,
                    });
                    setGameProgress({
                        wonGames: data.progress.completedCount,
                        totalGames: data.ladder.totalGames,
                        levelNumber: currentIdx + 1,
                        progress: Math.round((data.progress.completedCount / data.ladder.totalGames) * 100),
                        isCompleted: currentGame.isCompleted,
                        bestSteps: currentGame.bestSteps,
                    });
                    const ladderAlreadyComplete = data.progress.completedCount === data.ladder.totalGames;
                    setNavigation({
                        canGoBack: currentIdx > 0,
                        canGoNext: currentGame.isCompleted && currentIdx < data.ladder.games.length - 1,
                        isFirstGame: currentIdx === 0,
                        isLastGame: currentIdx === data.ladder.games.length - 1,
                        isLastLevel: currentIdx === data.ladder.games.length - 1,
                        ladderComplete: ladderAlreadyComplete,
                    });
                    // When the ladder is already complete on (re)load, the user finished
                    // it earlier — pre-dismiss the Marathon celebration so it doesn't
                    // re-pop on every reopen. It only shows the moment they complete it live.
                    setLadderCelebrationDismissed(ladderAlreadyComplete);
                    setCurrentPath([]);
                    setBackendConnected(true);
                    currentGameIdRef.current = currentGame.id;

                    // Restore saved game state (prefer localStorage - always fresh)
                    if (!currentGame.isCompleted) {
                        const localState = loadGameStateFromLocal('ranked', currentGame.startWord, currentGame.targetWord);
                        const serverState = currentGame.savedState;
                        setSavedGameState(localState || serverState || null);
                    } else {
                        setSavedGameState(null);
                        clearGameStateFromLocal(currentGame.startWord, currentGame.targetWord);
                    }

                    setHintStateForGame(currentGame.startWord, currentGame.targetWord, currentGame.isCompleted);
                    if (!currentGame.isCompleted) {
                        gameStartTimeRef.current = Date.now();
                    }
                    setNewlyEarnedAchievements([]);
                    setRatingUpdate(null);
                } else {
                    throw new Error(data.error || 'Failed to load ladder');
                }
            } else if (mode === 'challenge') {
                // Weekly challenge mode
                const data = await api.fetchChallenge(language);

                if (data.success && data.challenge && data.challenge.games.length > 0) {
                    setChallengeData(data.challenge);
                    setLadderData(null);
                    const currentIdx = data.progress.currentGameIndex;
                    setCurrentGameIndex(currentIdx);

                    const currentGame = data.challenge.games[currentIdx];

                    setGameData({
                        startWord: currentGame.startWord,
                        targetWord: currentGame.targetWord,
                        title: currentGame.title,
                        minSteps: currentGame.minSteps,
                        tier: currentGame.tier,
                        maxStars: currentGame.maxStars ?? 3,
                    });
                    setGameProgress({
                        wonGames: data.progress.completedCount,
                        totalGames: data.challenge.totalGames,
                        levelNumber: currentIdx + 1,
                        progress: Math.round((data.progress.completedCount / data.challenge.totalGames) * 100),
                        isCompleted: currentGame.isCompleted,
                        bestSteps: currentGame.bestSteps,
                    });
                    const challengeAlreadyComplete = data.progress.completedCount === data.challenge.totalGames;
                    setNavigation({
                        canGoBack: currentIdx > 0,
                        canGoNext: currentGame.isCompleted && currentIdx < data.challenge.games.length - 1,
                        isFirstGame: currentIdx === 0,
                        isLastGame: currentIdx === data.challenge.games.length - 1,
                        isLastLevel: currentIdx === data.challenge.games.length - 1,
                        ladderComplete: challengeAlreadyComplete,
                    });
                    // See ranked branch: pre-dismiss the completion celebration when the
                    // challenge is already finished so it doesn't re-pop on reopen.
                    setLadderCelebrationDismissed(challengeAlreadyComplete);
                    setCurrentPath([]);
                    setBackendConnected(true);
                    currentGameIdRef.current = currentGame.id;

                    // Restore saved game state (prefer localStorage - always fresh)
                    if (!currentGame.isCompleted) {
                        const localState = loadGameStateFromLocal('challenge', currentGame.startWord, currentGame.targetWord);
                        const serverState = currentGame.savedState;
                        setSavedGameState(localState || serverState || null);
                    } else {
                        setSavedGameState(null);
                        clearGameStateFromLocal(currentGame.startWord, currentGame.targetWord);
                    }

                    setHintStateForGame(currentGame.startWord, currentGame.targetWord, currentGame.isCompleted);
                    if (!currentGame.isCompleted) {
                        gameStartTimeRef.current = Date.now();
                    }
                    setNewlyEarnedAchievements([]);
                    setRatingUpdate(null);
                } else {
                    // No active challenge — show friendly message
                    setError(data.error || t('play.errors.noChallenge'));
                    setGameData(null);
                    setChallengeData(null);
                }
            } else if (mode === 'shared') {
                const data = await api.fetchShare(sharedSlug).catch(() => null);
                if (!data?.success || !data.share) {
                    window.history.replaceState({}, '', window.location.pathname);
                    setSharedSlug(null);
                    setMode('ranked');
                    return;
                }
                const shared = data.share;
                // The puzzle's dictionary drives the board; switch the UI language to match once.
                if (shared.language !== language && !sharedLanguageSetRef.current) {
                    sharedLanguageSetRef.current = true;
                    setLanguage(shared.language);
                }
                setSharedChallenge(shared);
                setSharedOutcome(null);
                setGameData({
                    startWord: shared.startWord,
                    targetWord: shared.targetWord,
                    title: `${shared.startWord} → ${shared.targetWord}`,
                    minSteps: shared.minSteps,
                    maxStars: 3,
                });
                setGameProgress({ wonGames: 0, totalGames: 0, levelNumber: 1, progress: 0, isCompleted: false, bestSteps: null });
                setNavigation({ canGoBack: false, canGoNext: false, isFirstGame: true, isLastGame: false });
                setCurrentPath([]);
                setBackendConnected(true);
                setLadderData(null);
                currentGameIdRef.current = null; // no server-side progress for shared puzzles
                setSavedGameState(loadGameStateFromLocal('shared', shared.startWord, shared.targetWord));
                setHintStateForGame(shared.startWord, shared.targetWord, false);
                gameStartTimeRef.current = Date.now();
                setNewlyEarnedAchievements([]);
                setRatingUpdate(null);
            } else if (mode === 'onboarding') {
                // Guided (step 1) / solo (step 2) onboarding puzzle.
                const step = onboardingStep === 2 ? 2 : 1;
                const data = await api.fetchGame(language, 'onboarding', { step });
                if (data.success && data.game) {
                    setGameData({
                        startWord: data.game.startWord,
                        targetWord: data.game.targetWord,
                        title: data.game.title,
                        minSteps: data.game.minSteps,
                        maxStars: data.game.maxStars ?? 3,
                        solutionPath: data.game.solutionPath,
                        coachingHints: data.game.coachingHints,
                    });
                    setGameProgress({ wonGames: 0, totalGames: 2, levelNumber: step, progress: 0, isCompleted: false, bestSteps: null });
                    setNavigation({ canGoBack: false, canGoNext: false, isFirstGame: true, isLastGame: false });
                    setCurrentPath([]);
                    setBackendConnected(true);
                    setLadderData(null);
                    currentGameIdRef.current = data.game.id;
                    setSavedGameState(null);
                    setHintStateForGame(data.game.startWord, data.game.targetWord, false);
                    gameStartTimeRef.current = Date.now();
                    setNewlyEarnedAchievements([]);
                    setRatingUpdate(null);
                } else {
                    throw new Error(data.error || 'Failed to load onboarding game');
                }
            } else {
                // Use existing API for practice/custom/expert modes (and legacy ranked)
                const backendMode = mode === 'practice' ? 'random' : mode;  // expert passes through as-is
                const effectiveDifficulty = mode === 'expert' ? expertMinSteps : difficulty;
                const data = await api.fetchGame(language, backendMode, {
                    difficulty: effectiveDifficulty,
                    startWord: customWords.start,
                    targetWord: customWords.target,
                });

                if (data.success && data.game) {
                    setGameData({
                        startWord: data.game.startWord,
                        targetWord: data.game.targetWord,
                        title: data.game.title,
                        minSteps: data.game.minSteps,
                        maxStars: data.game.maxStars ?? 3,
                    });
                    setGameProgress({
                        wonGames: data.progress.wonGames,
                        totalGames: data.progress.totalGames,
                        levelNumber: data.progress.levelNumber,
                        progress: data.progress.totalGames > 0
                            ? Math.round((data.progress.wonGames / data.progress.totalGames) * 100)
                            : 0,
                        isCompleted: data.progress.isCompleted,
                        bestSteps: data.progress.bestSteps,
                    });
                    setNavigation(data.navigation);
                    setCurrentPath([]);
                    setBackendConnected(true);
                    setLadderData(null);
                    currentGameIdRef.current = data.game.id;

                    // Restore saved game state (prefer localStorage - always fresh)
                    if (!data.progress.isCompleted) {
                        const localState = loadGameStateFromLocal(mode, data.game.startWord, data.game.targetWord);
                        const serverState = data.game.savedState;
                        setSavedGameState(localState || serverState || null);
                        setCompletedUserPath(null);
                    } else {
                        setSavedGameState(null);
                        setCompletedUserPath(data.game.userPath || null);
                        clearGameStateFromLocal(data.game.startWord, data.game.targetWord);
                    }

                    setHintStateForGame(data.game.startWord, data.game.targetWord, data.progress.isCompleted);
                    if (!data.progress.isCompleted) {
                        gameStartTimeRef.current = Date.now();
                    }
                    setNewlyEarnedAchievements([]);
                    setRatingUpdate(null);
                    // Enable Back if past games exist
                    if ((mode === 'practice' || mode === 'expert') && data.progress.wonGames > 0) {
                        setHasPastGames(true);
                    }
                } else {
                    throw new Error(data.error || 'Failed to load game');
                }
            }
        } catch (err) {
            console.error('Error loading game:', err);
            setBackendConnected(false);

            // Fallback: Try to get a random game from level 1
            try {
                // A random game has no coaching, so onboarding shows retry instead.
                if (mode === 'onboarding') throw err;
                const fallbackData = await api.fetchGame(language, 'random', { difficulty: 1 });
                if (fallbackData.success && fallbackData.game) {
                    setGameData({
                        startWord: fallbackData.game.startWord,
                        targetWord: fallbackData.game.targetWord,
                        title: fallbackData.game.title,
                        minSteps: fallbackData.game.minSteps,
                        maxStars: fallbackData.game.maxStars ?? 3,
                    });
                    setGameProgress({
                        wonGames: 0,
                        totalGames: 0,
                        levelNumber: 1,
                        progress: 0,
                        isCompleted: false,
                        bestSteps: null,
                    });
                    setNavigation({
                        canGoBack: false,
                        canGoNext: false,
                        isFirstGame: true,
                        isLastGame: false,
                    });
                    setBackendConnected(true);
                    setLadderData(null);
                } else {
                    throw new Error('Fallback game fetch failed');
                }
            } catch (fallbackErr) {
                console.error('Fallback game fetch also failed:', fallbackErr);
                setError(t('play.errors.noGames'));
                setGameData(null);
            }
        } finally {
            setLoading(false);
            modeSwitchRef.current = false;
        }
    }, [language, setLanguage, t, mode, sharedSlug, onboardingStep, difficulty, expertMinSteps, customWords, setHintStateForGame, USE_NEW_LADDER, loadGameStateFromLocal, clearGameStateFromLocal]);

    useEffect(() => {
        loadGame();
    }, [loadGame]);

    // Animate challenge banner scroll via Web Animations API
    // (CSS var() inside @keyframes doesn't work in Android WebView)
    useEffect(() => {
        if (mode !== 'challenge' || !challengeData) return;
        let anim;
        let ro;
        const measure = () => {
            const el = challengeBannerRef.current;
            if (!el) return;
            const parent = el.parentElement;
            if (!parent) return;
            const elWidth = el.scrollWidth;
            const parentWidth = parent.clientWidth;
            if (anim) { anim.cancel(); anim = null; }
            if (elWidth > parentWidth) {
                const offset = -(elWidth - parentWidth + 12);
                anim = el.animate([
                    { transform: 'translateX(0)' },
                    { transform: `translateX(${offset}px)`, offset: 0.4 },
                    { transform: `translateX(${offset}px)`, offset: 0.6 },
                    { transform: 'translateX(0)' }
                ], { duration: 8000, iterations: Infinity, delay: 2000 });
            }
        };
        // Use ResizeObserver to catch when the element is actually in the DOM and laid out
        const startObserving = () => {
            const el = challengeBannerRef.current;
            if (el) {
                ro = new ResizeObserver(() => measure());
                ro.observe(el.parentElement || el);
                measure(); // also measure immediately
            }
        };
        // The ref may not be set yet on first render, poll briefly
        const t1 = setTimeout(startObserving, 100);
        const t2 = setTimeout(measure, 500);
        const t3 = setTimeout(measure, 1500);
        return () => {
            clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
            if (anim) anim.cancel();
            if (ro) ro.disconnect();
        };
    }, [mode, challengeData]);

    // Fetch rank progress on mount
    useEffect(() => {
        if (backendConnected) {
            api.fetchRankProgress().then(res => {
                if (res?.rankProgress) setRankProgress(res.rankProgress);
                if (res?.decayUpdate) setDecayUpdate(res.decayUpdate);
            }).catch(() => {});
        }
    }, [backendConnected]);

    // Flush any queued ranked wins (B6) on mount and when connectivity returns.
    useEffect(() => {
        const flush = () => flushSubmitOutbox(OUTBOX_SUBMITTERS);
        flush();
        window.addEventListener('online', flush);
        return () => window.removeEventListener('online', flush);
    }, []);

    // Auto-recover when connectivity returns (B7); in demo mode also poll health, since a deploy restart fires no 'online' event.
    useEffect(() => {
        if (!error && backendConnected) return; // healthy — nothing to recover
        let cancelled = false;
        let checking = false;
        const recover = async () => {
            if (checking) return;
            checking = true;
            try {
                if (!backendConnected && !(await api.pingBackend())) return;
                if (cancelled) return;
                await flushSubmitOutbox(OUTBOX_SUBMITTERS);
                if (cancelled) return;
                setBackendConnected(true);
                if (error) loadGame();
            } finally {
                checking = false;
            }
        };
        const onVisible = () => { if (document.visibilityState === 'visible') recover(); };
        window.addEventListener('online', recover);
        let timer = null;
        if (!backendConnected) {
            timer = setInterval(recover, 15000);
            document.addEventListener('visibilitychange', onVisible);
        }
        return () => {
            cancelled = true;
            window.removeEventListener('online', recover);
            document.removeEventListener('visibilitychange', onVisible);
            if (timer) clearInterval(timer);
        };
    }, [error, backendConnected, loadGame]);

    // B20: load practice stats in practice mode; refresh when a game is won
    // (gameProgress.wonGames changes) so a new personal best shows immediately.
    useEffect(() => {
        if (mode === 'practice' && backendConnected) {
            api.fetchPracticeStats(language).then(s => { if (s) setPracticeStats(s); });
        }
    }, [mode, backendConnected, language, gameProgress.wonGames]);

    // Persist the onboarding step so a mid-flow refresh resumes at the right
    // screen (B8). Only writes while onboarding or once completed/skipped; the
    // mode initializer reads this same key to keep a resumed user in onboarding.
    useEffect(() => {
        try {
            if (mode === 'onboarding' || onboardingStep === 'done' || onboardingStep === 'skipped') {
                localStorage.setItem('wl_onboarding_step', String(onboardingStep));
            }
        } catch { /* ignore */ }
    }, [onboardingStep, mode]);

    // Reload game data when user logs in (player entity changed, data may have been migrated)
    const prevAuthenticatedRef = useRef(isAuthenticated);
    useEffect(() => {
        if (isAuthenticated && !prevAuthenticatedRef.current) {
            loadGame();
        }
        prevAuthenticatedRef.current = isAuthenticated;
    }, [isAuthenticated, loadGame]);

    // Staff-only: load a specific custom puzzle from URL params (?start_word=X&target_word=Y).
    // Used by the tools/video/ marketing-video automation. No-op for non-staff users.
    const customParamsAppliedRef = useRef(false);
    useEffect(() => {
        if (customParamsAppliedRef.current) return;
        if (!user?.is_staff) return;
        const params = new URLSearchParams(window.location.search);
        const start = upperWord((params.get('start_word') || '').trim());
        const target = upperWord((params.get('target_word') || '').trim());
        if (!start || !target) return;
        customParamsAppliedRef.current = true;
        setMode('custom');
        setCustomWords({ start, target });
    }, [user]);

    const handleGameWon = useCallback(async (path, meta = {}) => {
        setCurrentPath(path);
        const distanceIndicatorUsed = meta.distanceIndicatorUsed !== false;

        // Get elapsed time before clearing state
        const elapsed = gameRef.current?.getElapsedSeconds?.() ?? null;

        // Clear saved game state since game is completed
        if (gameData) {
            clearGameStateFromLocal(gameData.startWord, gameData.targetWord);
            setSavedGameState(null);
        }
        // Cancel any pending server save
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
        }

        // Clear saved hints since game is completed
        if (gameData) {
            clearHintState(gameData.startWord, gameData.targetWord);
        }

        // Track game completion
        trackEvent('Game Complete', {
            mode,
            language,
            steps: String(path.length - 1),
            min_steps: String(gameData?.minSteps ?? ''),
            hints_used: String(hintsUsed),
        });

        // Ask for feedback on the 5th win, then every 25th, at most once per 30 days.
        let winsTotal = 0;
        if (mode !== 'onboarding') {
            try {
                const wins = (parseInt(localStorage.getItem('wl_wins_total') || '0', 10) || 0) + 1;
                winsTotal = wins;
                localStorage.setItem('wl_wins_total', String(wins));
                const askedAt = Date.parse(localStorage.getItem('wl_feedback_asked_at') || '') || 0;
                if (Date.now() - askedAt > 30 * 24 * 3600 * 1000 && (wins === 5 || (wins > 5 && wins % 25 === 0))) {
                    setVictoryFeedback(true);
                    updatePreferenceRef.current('feedbackAskedAt', new Date().toISOString());
                    trackEvent('Feedback Shown', { context: 'victory', wins: String(wins) });
                }
            } catch { /* ignore */ }
        }

        // B15: sign-up nudge for guests on their 3rd win (on the 1st it cost first-timers game 2), once per browser.
        // Not during onboarding or on a friend's puzzle.
        if (!isAuthenticated && mode !== 'onboarding' && mode !== 'shared' && winsTotal >= 3) {
            try {
                if (!localStorage.getItem('wl_first_win_prompted')) {
                    localStorage.setItem('wl_first_win_prompted', 'true');
                    setTimeout(() => {
                        promptLogin(t('play.signupPrompt.firstWin'), { signup: true });
                    }, 1800);
                }
            } catch { /* ignore */ }
        }

        // Onboarding: record the step server-side (fires the funnel event) and
        // advance the guided flow. No ranking/achievements for onboarding games.
        if (mode === 'onboarding') {
            const submitted = backendConnected
                ? api.submitGame(language, path, 'onboarding', elapsed, distanceIndicatorUsed, onboardingStep).catch(() => {})
                : Promise.resolve();
            setGameProgress(prev => ({ ...prev, isCompleted: true }));
            if (onboardingStep === 1) {
                // Let the solved ladder show a moment, and let the save land before step 2 takes over the active row.
                const pause = ms => new Promise(r => setTimeout(r, ms));
                await Promise.all([pause(900), Promise.race([submitted, pause(2500)])]);
                setOnboardingStep(2);
            } else {
                trackEvent('onboarding_ladder_directed', {});
                setOnboardingStep(3);
            }
            return;
        }

        // Friend's shared puzzle: compare with their result; it never touches progress or rank.
        if (mode === 'shared' && sharedChallenge) {
            const steps = path.length - 1;
            setGameProgress(prev => ({ ...prev, isCompleted: true, bestSteps: steps }));
            setNavigation(prev => ({ ...prev, canGoNext: true }));
            const local = steps < sharedChallenge.playerSteps ? 'beat' : steps === sharedChallenge.playerSteps ? 'tied' : 'behind';
            setSharedOutcome({ outcome: local, steps, sharerSteps: sharedChallenge.playerSteps });
            api.submitShareResult(sharedChallenge.slug, path).catch(() => {});
            return;
        }

        // Weekly challenge: update local challenge data and submit to backend
        if (mode === 'challenge' && challengeData) {
            setChallengeData(prev => {
                if (!prev) return prev;
                const updatedGames = [...prev.games];
                updatedGames[currentGameIndex] = {
                    ...updatedGames[currentGameIndex],
                    isCompleted: true,
                    bestSteps: path.length - 1,
                    userPath: path.map(w => upperWord(w)),
                };
                return { ...prev, games: updatedGames };
            });

            const alreadyCompleted = challengeData.games[currentGameIndex].isCompleted;
            const newWonGames = alreadyCompleted ? gameProgress.wonGames : gameProgress.wonGames + 1;
            setGameProgress(prev => ({
                ...prev,
                isCompleted: true,
                wonGames: newWonGames,
                bestSteps: path.length - 1,
                progress: Math.round((newWonGames / challengeData.totalGames) * 100),
            }));

            const isLast = currentGameIndex === challengeData.games.length - 1;
            setNavigation(prev => ({
                ...prev,
                canGoNext: !isLast,
                ladderComplete: newWonGames === challengeData.totalGames,
            }));

            const submitItem = { lang: language, gameIndex: currentGameIndex, steps: path, elapsed, distanceIndicatorUsed };
            if (backendConnected) {
                api.submitChallengeGame(language, currentGameIndex, path, elapsed, distanceIndicatorUsed).then(res => {
                    // Capture rank progress update when available
                    if (res?.rankProgress) setRankProgress(res.rankProgress);
                    if (res?.rankProgressUpdate) {
                        setRankProgressUpdate(res.rankProgressUpdate);
                        if (res.rankProgressUpdate.rankChanged) {
                            setShowRankUpCelebration(true);
                        }
                    }
                    // Show hint earned notification
                    if (res?.hintAwarded) {
                        setHintEarnedToast(res.hintAwarded);
                    }
                }).catch(err => {
                    console.error('Error submitting challenge game:', err);
                    if (isPermanentRejection(err)) return;
                    setBackendConnected(false);
                    enqueueSubmit('challenge', submitItem); // don't lose the win
                });
            } else {
                // Backend known-down: queue the win so it syncs on reconnect.
                enqueueSubmit('challenge', submitItem);
            }
        }
        // New ladder system: update local ladder data and submit to backend
        else if (USE_NEW_LADDER && mode === 'ranked' && ladderData) {
            // Update local ladder data immediately for instant UI feedback
            setLadderData(prev => {
                if (!prev) return prev;
                const updatedGames = [...prev.games];
                updatedGames[currentGameIndex] = {
                    ...updatedGames[currentGameIndex],
                    isCompleted: true,
                    bestSteps: path.length - 1,
                    userPath: path.map(w => upperWord(w)),
                };
                return { ...prev, games: updatedGames };
            });

            const alreadyCompleted = ladderData.games[currentGameIndex].isCompleted;
            const newWonGames = alreadyCompleted ? gameProgress.wonGames : gameProgress.wonGames + 1;
            setGameProgress(prev => ({
                ...prev,
                isCompleted: true,
                wonGames: newWonGames,
                bestSteps: path.length - 1,
                progress: Math.round((newWonGames / ladderData.totalGames) * 100),
            }));

            // Update navigation
            const isLast = currentGameIndex === ladderData.games.length - 1;
            setNavigation(prev => ({
                ...prev,
                canGoNext: !isLast,
                ladderComplete: newWonGames === ladderData.totalGames,
            }));

            // Submit to backend. Queue for retry on failure / when offline so a
            // won ranked game is never silently lost (B6). Backend guards replays.
            const submitItem = { lang: language, gameIndex: currentGameIndex, steps: path, elapsed, distanceIndicatorUsed };
            const isLadderComplete = newWonGames === ladderData.totalGames;
            if (isLadderComplete) {
                trackEvent('Ladder Complete', { language, total_games: String(ladderData.totalGames) });
                // App: the OS rating sheet, never right after an opinion question (store policy). Else ask unrated players.
                if (canRequestStoreReview()) {
                    setTimeout(() => requestStoreReview('ladder_complete'), 1500);
                } else if (!localStorage.getItem('wl_feedback_rated_at')) {
                    setVictoryFeedback(false);
                    setLadderFeedback(true);
                    trackEvent('Feedback Shown', { context: 'ladder_complete' });
                }
            }
            if (backendConnected) {
                api.submitLadderGame(language, currentGameIndex, path, elapsed, distanceIndicatorUsed).then(res => {
                    // Update rank progress from response
                    if (res?.rankProgress) setRankProgress(res.rankProgress);
                    if (res?.rankProgressUpdate) {
                        setRankProgressUpdate(res.rankProgressUpdate);
                        if (res.rankProgressUpdate.rankChanged) {
                            setShowRankUpCelebration(true);
                        }
                    }
                    // Update skip availability
                    if (res?.skipAvailable !== undefined) setSkipAvailable(res.skipAvailable);
                    // Capture rating update
                    if (res?.ratingUpdate) setRatingUpdate(res.ratingUpdate);
                    // Streak now ticks on the first daily game (B11), so process
                    // any streak change the server reports — not only on the full
                    // ladder. `changed` is false on the idempotent same-day no-op.
                    if (res?.streakUpdate?.changed) {
                        refreshStreak();
                        if (res.streakUpdate.claimable_milestones?.length > 0) {
                            setPendingMilestones(res.streakUpdate.claimable_milestones);
                        }
                    }
                    // Show hint earned notification
                    if (res?.hintAwarded) {
                        setHintEarnedToast(res.hintAwarded);
                    }
                }).catch(err => {
                    console.error('Error submitting ladder game:', err);
                    if (isPermanentRejection(err)) return;
                    setBackendConnected(false);
                    enqueueSubmit('ladder', submitItem); // don't lose the win
                });
            } else {
                // Backend known-down: queue the win so it syncs on reconnect.
                enqueueSubmit('ladder', submitItem);
            }
        } else if (backendConnected) {
            // Legacy submission for other modes (practice, expert, custom)
            try {
                const backendMode = mode === 'practice' ? 'random' : mode;  // expert passes through as-is
                const response = await api.submitGame(language, path, backendMode, elapsed, distanceIndicatorUsed);
                if (response.success) {
                    setGameProgress(prev => ({
                        ...prev,
                        isCompleted: true,
                        wonGames: response.progress?.wonGames ?? prev.wonGames + 1,
                        progress: response.progress?.totalGames > 0
                            ? Math.round((response.progress.wonGames / response.progress.totalGames) * 100)
                            : prev.progress,
                        bestSteps: response.progress?.bestSteps,
                    }));
                    // Update rank progress from response
                    if (response.rankProgress) setRankProgress(response.rankProgress);
                    if (response.rankProgressUpdate) {
                        setRankProgressUpdate(response.rankProgressUpdate);
                        if (response.rankProgressUpdate.rankChanged) {
                            setShowRankUpCelebration(true);
                        }
                    }
                    // Capture rating update (expert mode)
                    if (response.ratingUpdate) setRatingUpdate(response.ratingUpdate);
                    if (response.navigation) {
                        setNavigation(response.navigation);
                    } else {
                        setNavigation(prev => ({
                            ...prev,
                            canGoNext: true, // always allow next in practice/expert
                        }));
                    }
                }
            } catch (err) {
                console.error('Error submitting game:', err);
                setGameProgress(prev => ({
                    ...prev,
                    isCompleted: true,
                    wonGames: prev.wonGames + 1,
                    progress: prev.totalGames > 0
                        ? Math.round(((prev.wonGames + 1) / prev.totalGames) * 100)
                        : 0,
                }));
                setNavigation(prev => ({
                    ...prev,
                    canGoNext: !prev.isLastGame && !prev.ladderComplete,
                }));
            }
        } else {
            // No backend - just update locally
            setGameProgress(prev => ({
                ...prev,
                isCompleted: true,
                wonGames: prev.wonGames + 1,
                progress: prev.totalGames > 0
                    ? Math.round(((prev.wonGames + 1) / prev.totalGames) * 100)
                    : 0,
            }));
            setNavigation(prev => ({
                ...prev,
                canGoNext: !prev.isLastGame && !prev.ladderComplete,
            }));
        }

        // Detect achievements
        const achievements = [];
        const userSteps = path.length - 1;

        // Perfect Game: optimal steps + no hints
        if (gameData && userSteps === gameData.minSteps && hintsUsed === 0) {
            achievements.push('perfect_game');
        }

        // Lightning Fast: under 30 seconds
        if (gameStartTimeRef.current) {
            const elapsed = (Date.now() - gameStartTimeRef.current) / 1000;
            if (elapsed < 30) {
                achievements.push('lightning_fast');
            }
        }

        // Marathon: all ladder games done (only in ranked)
        if (USE_NEW_LADDER && mode === 'ranked' && ladderData) {
            const wasAlreadyCompleted = ladderData.games[currentGameIndex].isCompleted;
            const newWonGamesCount = wasAlreadyCompleted ? gameProgress.wonGames : gameProgress.wonGames + 1;
            if (newWonGamesCount === ladderData.totalGames) {
                achievements.push('marathon');
            }
        }

        if (achievements.length > 0) {
            setNewlyEarnedAchievements(achievements);
        }

        // Refresh streak after win (non-blocking)
        if (backendConnected) {
            refreshStreak();
        }

        // Refresh ads on game completion (natural breakpoint)
        refreshAds();
    }, [language, backendConnected, mode, onboardingStep, gameData, clearHintState, clearGameStateFromLocal, refreshAds, USE_NEW_LADDER, ladderData, currentGameIndex, gameProgress.wonGames, refreshStreak, hintsUsed, challengeData, sharedChallenge, isAuthenticated, promptLogin, t]);

    const handleSkipGame = useCallback(async () => {
        if (!ladderData || !skipAvailable || mode !== 'ranked') return;
        try {
            const res = await api.skipLadderGame(language, currentGameIndex);
            if (res.success) {
                setSkipAvailable(false);
                setSkippedGameIndex(currentGameIndex);
                // Clear saved game state for skipped game
                if (gameData) {
                    clearGameStateFromLocal(gameData.startWord, gameData.targetWord);
                    setSavedGameState(null);
                }
                // Update local ladder data
                setLadderData(prev => {
                    if (!prev) return prev;
                    const updatedGames = [...prev.games];
                    updatedGames[currentGameIndex] = {
                        ...updatedGames[currentGameIndex],
                        isCompleted: true,
                        isSkipped: true,
                        solutionPath: res.ladder?.games?.[currentGameIndex]?.solutionPath || [],
                        bestSteps: 999,
                    };
                    return { ...prev, games: updatedGames };
                });
                const alreadyCompleted = ladderData.games[currentGameIndex].isCompleted;
                const newWonGames = alreadyCompleted ? gameProgress.wonGames : gameProgress.wonGames + 1;
                setGameProgress(prev => ({
                    ...prev,
                    isCompleted: true,
                    wonGames: newWonGames,
                    bestSteps: 999,
                    progress: Math.round((newWonGames / ladderData.totalGames) * 100),
                }));
                const isLast = currentGameIndex === ladderData.games.length - 1;
                setNavigation(prev => ({
                    ...prev,
                    canGoNext: !isLast,
                    ladderComplete: newWonGames === ladderData.totalGames,
                }));
                // Update rank progress if returned
                if (res.rankProgress) setRankProgress(res.rankProgress);
            }
        } catch (err) {
            console.error('Skip failed:', err);
        }
    }, [language, currentGameIndex, ladderData, skipAvailable, mode, gameProgress.wonGames]);

    const executeNextGame = useCallback(async () => {
        // Cancel any pending debounced save to prevent stale writes
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        // Save current game state before navigating away
        saveGameStateToServer(true);

        // Weekly challenge: instant local navigation
        if (mode === 'challenge' && challengeData) {
            const nextIdx = currentGameIndex + 1;
            if (nextIdx < challengeData.games.length) {
                const nextGame = challengeData.games[nextIdx];
                setCurrentGameIndex(nextIdx);
                currentGameIdRef.current = nextGame.id;
                setGameData({
                    startWord: nextGame.startWord,
                    targetWord: nextGame.targetWord,
                    title: nextGame.title,
                    minSteps: nextGame.minSteps,
                    tier: nextGame.tier,
                    maxStars: nextGame.maxStars ?? 3,
                });
                setGameProgress(prev => ({
                    ...prev,
                    levelNumber: nextIdx + 1,
                    isCompleted: nextGame.isCompleted,
                    bestSteps: nextGame.bestSteps,
                }));
                setNavigation({
                    canGoBack: nextIdx > 0,
                    canGoNext: nextGame.isCompleted && nextIdx < challengeData.games.length - 1,
                    isFirstGame: nextIdx === 0,
                    isLastGame: nextIdx === challengeData.games.length - 1,
                    isLastLevel: nextIdx === challengeData.games.length - 1,
                    ladderComplete: gameProgress.wonGames === challengeData.totalGames,
                });
                setCurrentPath([]);
                // Restore saved game state for next game (prefer localStorage - always fresh)
                if (!nextGame.isCompleted) {
                    const localState = loadGameStateFromLocal('challenge', nextGame.startWord, nextGame.targetWord);
                    const serverState = nextGame.savedState;
                    setSavedGameState(localState || serverState || null);
                } else {
                    setSavedGameState(null);
                }
                setHintStateForGame(nextGame.startWord, nextGame.targetWord, nextGame.isCompleted);
                setRetryCount(0);
                if (!nextGame.isCompleted) {
                    gameStartTimeRef.current = Date.now();
                }
                setNewlyEarnedAchievements([]);
                setRatingUpdate(null);
            }
            return;
        }

        // New ladder system: instant local navigation
        if (USE_NEW_LADDER && mode === 'ranked' && ladderData) {
            const nextIdx = currentGameIndex + 1;
            if (nextIdx < ladderData.games.length) {
                const nextGame = ladderData.games[nextIdx];
                setCurrentGameIndex(nextIdx);
                currentGameIdRef.current = nextGame.id;
                setGameData({
                    startWord: nextGame.startWord,
                    targetWord: nextGame.targetWord,
                    title: nextGame.title,
                    minSteps: nextGame.minSteps,
                    tier: nextGame.tier,
                    maxStars: nextGame.maxStars ?? 3,
                });
                setGameProgress(prev => ({
                    ...prev,
                    levelNumber: nextIdx + 1,
                    isCompleted: nextGame.isCompleted,
                    bestSteps: nextGame.bestSteps,
                }));
                setNavigation({
                    canGoBack: nextIdx > 0,
                    canGoNext: nextGame.isCompleted && nextIdx < ladderData.games.length - 1,
                    isFirstGame: nextIdx === 0,
                    isLastGame: nextIdx === ladderData.games.length - 1,
                    isLastLevel: nextIdx === ladderData.games.length - 1,
                    ladderComplete: gameProgress.wonGames === ladderData.totalGames,
                });
                setCurrentPath([]);
                // Restore saved game state for next game (prefer localStorage - always fresh)
                if (!nextGame.isCompleted) {
                    const localState = loadGameStateFromLocal('ranked', nextGame.startWord, nextGame.targetWord);
                    const serverState = nextGame.savedState;
                    setSavedGameState(localState || serverState || null);
                } else {
                    setSavedGameState(null);
                }
                setHintStateForGame(nextGame.startWord, nextGame.targetWord, nextGame.isCompleted);
                setRetryCount(0);
                if (!nextGame.isCompleted) {
                    gameStartTimeRef.current = Date.now();
                }
                setNewlyEarnedAchievements([]);
                setRatingUpdate(null);
            }
            return; // No API call needed - instant!
        }

        // Legacy API for other modes (practice, expert, custom)
        setLoading(true);
        try {
            const backendMode = mode === 'practice' ? 'random' : mode;  // expert passes through as-is
            const effectiveDifficulty = mode === 'expert' ? expertMinSteps : difficulty;
            const response = await api.nextGame(language, backendMode, {
                difficulty: effectiveDifficulty,
                startWord: customWords.start,
                targetWord: customWords.target,
            });

            if (response.success && response.game) {
                setGameData({
                    startWord: response.game.startWord,
                    targetWord: response.game.targetWord,
                    title: response.game.title,
                    minSteps: response.game.minSteps,
                    maxStars: response.game.maxStars ?? 3,
                });
                setGameProgress(prev => ({
                    ...prev,
                    isCompleted: response.progress.isCompleted,
                    bestSteps: response.progress.bestSteps,
                }));
                // Update rank progress from response
                if (response.rankProgress) setRankProgress(response.rankProgress);
                setCurrentPath([]);

                setHintStateForGame(response.game.startWord, response.game.targetWord, response.progress.isCompleted);
                setRetryCount(0);
                gameStartTimeRef.current = Date.now();
                setNewlyEarnedAchievements([]);

                if (mode === 'practice') {
                    setDifficulty(response.progress.levelNumber);
                }
            }
        } catch (err) {
            console.error('Error getting next game:', err);
        } finally {
            setLoading(false);
        }
    }, [language, gameProgress.wonGames, mode, difficulty, expertMinSteps, customWords, setHintStateForGame, USE_NEW_LADDER, ladderData, challengeData, currentGameIndex, loadGameStateFromLocal, saveGameStateToServer]);

    const clearSharedLink = useCallback(() => {
        try {
            const url = new URL(window.location.href);
            url.searchParams.delete('share');
            window.history.replaceState({}, '', url.pathname + url.search);
        } catch { /* ignore */ }
        setSharedSlug(null);
        setSharedChallenge(null);
        setSharedOutcome(null);
    }, []);

    const handleNextGame = useCallback(async () => {
        // Allow navigation if current game is completed OR was already completed in ladder/challenge
        const alreadyPassed =
            (mode === 'ranked' && ladderData?.games?.[currentGameIndex]?.isCompleted) ||
            (mode === 'challenge' && challengeData?.games?.[currentGameIndex]?.isCompleted);
        if (!gameProgress.isCompleted && !alreadyPassed) return;

        // Block navigation if there's a pending rank promotion
        if (ratingUpdate?.tierChanged) {
            setRankPromotionData(ratingUpdate);
            setShowRankPromotion(true);
            return;
        }

        executeNextGame();
    }, [gameProgress.isCompleted, ratingUpdate, executeNextGame, mode, ladderData, challengeData, currentGameIndex]);

    const handleRankPromotionDismiss = useCallback(() => {
        setShowRankPromotion(false);
        setRankPromotionData(null);
        executeNextGame();
    }, [executeNextGame]);

    const handlePreviousGame = useCallback(async () => {
        if (!navigation.canGoBack) return;

        // Weekly challenge: instant local navigation
        if (mode === 'challenge' && challengeData) {
            // Save current game state before navigating
            saveGameStateToServer(true);

            const prevIdx = currentGameIndex - 1;
            if (prevIdx >= 0) {
                const prevGame = challengeData.games[prevIdx];
                setCurrentGameIndex(prevIdx);
                currentGameIdRef.current = prevGame.id;
                setGameData({
                    startWord: prevGame.startWord,
                    targetWord: prevGame.targetWord,
                    title: prevGame.title,
                    minSteps: prevGame.minSteps,
                    tier: prevGame.tier,
                    maxStars: prevGame.maxStars ?? 3,
                });
                setGameProgress(prev => ({
                    ...prev,
                    levelNumber: prevIdx + 1,
                    isCompleted: prevGame.isCompleted,
                    bestSteps: prevGame.bestSteps,
                }));
                setNavigation({
                    canGoBack: prevIdx > 0,
                    canGoNext: prevGame.isCompleted && prevIdx < challengeData.games.length - 1,
                    isFirstGame: prevIdx === 0,
                    isLastGame: prevIdx === challengeData.games.length - 1,
                    isLastLevel: prevIdx === challengeData.games.length - 1,
                    ladderComplete: gameProgress.wonGames === challengeData.totalGames,
                });
                setCurrentPath([]);
                // Restore saved game state for previous game (prefer localStorage - always fresh)
                if (!prevGame.isCompleted) {
                    const localState = loadGameStateFromLocal('challenge', prevGame.startWord, prevGame.targetWord);
                    const serverState = prevGame.savedState;
                    setSavedGameState(localState || serverState || null);
                } else {
                    setSavedGameState(null);
                }
                setHintStateForGame(prevGame.startWord, prevGame.targetWord, prevGame.isCompleted);
                setRatingUpdate(null);
            }
            return;
        }

        // New ladder system: instant local navigation
        if (USE_NEW_LADDER && mode === 'ranked' && ladderData) {
            // Save current game state before navigating
            saveGameStateToServer(true);

            const prevIdx = currentGameIndex - 1;
            if (prevIdx >= 0) {
                const prevGame = ladderData.games[prevIdx];
                setCurrentGameIndex(prevIdx);
                currentGameIdRef.current = prevGame.id;
                setGameData({
                    startWord: prevGame.startWord,
                    targetWord: prevGame.targetWord,
                    title: prevGame.title,
                    minSteps: prevGame.minSteps,
                    tier: prevGame.tier,
                    maxStars: prevGame.maxStars ?? 3,
                });
                setGameProgress(prev => ({
                    ...prev,
                    levelNumber: prevIdx + 1,
                    isCompleted: prevGame.isCompleted,
                    bestSteps: prevGame.bestSteps,
                }));
                setNavigation({
                    canGoBack: prevIdx > 0,
                    canGoNext: prevGame.isCompleted && prevIdx < ladderData.games.length - 1,
                    isFirstGame: prevIdx === 0,
                    isLastGame: prevIdx === ladderData.games.length - 1,
                    isLastLevel: prevIdx === ladderData.games.length - 1,
                    ladderComplete: gameProgress.wonGames === ladderData.totalGames,
                });
                setCurrentPath([]);
                // Restore saved game state for previous game (prefer localStorage - always fresh)
                if (!prevGame.isCompleted) {
                    const localState = loadGameStateFromLocal('ranked', prevGame.startWord, prevGame.targetWord);
                    const serverState = prevGame.savedState;
                    setSavedGameState(localState || serverState || null);
                } else {
                    setSavedGameState(null);
                }
                setHintStateForGame(prevGame.startWord, prevGame.targetWord, prevGame.isCompleted);
                setRatingUpdate(null);
            }
            return; // No API call needed - instant!
        }

        // Legacy API for other modes
        setLoading(true);
        try {
            const backendMode = mode === 'practice' ? 'random' : mode;  // expert passes through as-is
            const response = await api.previousGame(language, backendMode);

            if (response.success && response.game) {
                setGameData({
                    startWord: response.game.startWord,
                    targetWord: response.game.targetWord,
                    title: response.game.title,
                    minSteps: response.game.minSteps,
                    maxStars: response.game.maxStars ?? 3,
                });
                setGameProgress({
                    wonGames: response.progress.wonGames,
                    totalGames: response.progress.totalGames,
                    levelNumber: response.progress.levelNumber,
                    progress: response.progress.totalGames > 0
                        ? Math.round((response.progress.wonGames / response.progress.totalGames) * 100)
                        : 0,
                    isCompleted: response.progress.isCompleted,
                    bestSteps: response.progress.bestSteps,
                });
                setNavigation(response.navigation);
                setCurrentPath([]);

                setHintStateForGame(response.game.startWord, response.game.targetWord, response.progress.isCompleted);
            }
        } catch (err) {
            console.error('Error going back:', err);
        } finally {
            setLoading(false);
        }
    }, [language, navigation.canGoBack, mode, setHintStateForGame, USE_NEW_LADDER, ladderData, challengeData, currentGameIndex, gameProgress.wonGames, saveGameStateToServer, loadGameStateFromLocal]);

    const handleModeChange = (newMode) => {
        if (newMode === mode) return;
        if (mode === 'shared') clearSharedLink();
        // Save current game state before switching modes
        saveGameStateToServer(true);
        setLoading(true);
        modeSwitchRef.current = true;
        setMode(newMode);
        // Don't setGameData(null) — keep old game visible under blur overlay
        setDifficulty(1);
        setExpertMinSteps(8);
        // Reset ladder and challenge data when changing mode
        setLadderData(null);
        setChallengeData(null);
        setCurrentGameIndex(0);
        // Reset hints when changing mode
        setHintsUsed(0);
        setCurrentHint(null);
        setLastHint(null);
        setHintHistory([]);
        setTotalPenaltyPercent(0);
        setHintError(null);
        // Reset rank up celebration tracking
        setShowRankUpCelebration(false);
        setRankProgressUpdate(null);
        setRatingUpdate(null);
        setLadderCelebrationDismissed(false);
        // Clear game history
        clearHistory();
        setSavedGameState(null);
        // Refresh ads on mode switch (natural breakpoint)
        refreshAds();
    };

    // Handle expert mode difficulty change (easier/same/harder)
    const handleExpertDifficulty = useCallback(async (preference) => {
        let newMinSteps = expertMinSteps;
        if (preference === 'harder') {
            newMinSteps = Math.min(expertMinSteps + 2, 20);
        } else if (preference === 'easier') {
            newMinSteps = Math.max(expertMinSteps - 2, 8);
        }
        // Load next expert game with the new difficulty
        // Update expertMinSteps after the API response to avoid triggering loadGame race condition
        setLoading(true);
        try {
            const response = await api.nextGame(language, 'expert', {
                difficulty: newMinSteps,
            });
            if (response.success && response.game) {
                setExpertMinSteps(newMinSteps);
                setGameData({
                    startWord: response.game.startWord,
                    targetWord: response.game.targetWord,
                    title: response.game.title,
                    minSteps: response.game.minSteps,
                    maxStars: response.game.maxStars ?? 3,
                });
                setGameProgress(prev => ({
                    ...prev,
                    isCompleted: response.progress.isCompleted,
                    bestSteps: response.progress.bestSteps,
                }));
                if (response.rankProgress) setRankProgress(response.rankProgress);
                setCurrentPath([]);
                setHintStateForGame(response.game.startWord, response.game.targetWord, response.progress.isCompleted);
                setRetryCount(0);
                gameStartTimeRef.current = Date.now();
                setNewlyEarnedAchievements([]);
                setRankProgressUpdate(null);
                setHintsUsed(0);
                setCurrentHint(null);
                setLastHint(null);
                setHintHistory([]);
                setTotalPenaltyPercent(0);
                setHintError(null);
                resetGameHintStatus();
            }
        } catch (err) {
            console.error('Error getting expert game:', err);
            setError(t('play.errors.noExpertGame'));
            setTimeout(() => setError(null), 3000);
        } finally {
            setLoading(false);
        }
    }, [language, t, expertMinSteps, setHintStateForGame, resetGameHintStatus]);

    // Flag: auto-apply the next hint (set when hint comes from ad/purchase flow)
    const autoApplyNextHint = useRef(false);

    // Opens the unlock or purchase flow when the wallet is empty. Returns whether a hint can be spent.
    const ensureHintAvailable = useCallback(() => {
        if (autoApplyNextHint.current) return true;
        if (canUseFreeHint || hasAdHintAvailable || hintBalance > 0) return true;
        if (isNativeMobile() || !purchasesEnabled) openHintUnlockModal(); else openPurchaseModal();
        return false;
    }, [canUseFreeHint, hasAdHintAvailable, hintBalance, openHintUnlockModal, openPurchaseModal, isNativeMobile, purchasesEnabled]);

    // Spends one hint from the wallet; null when nothing was granted (the modal is already showing).
    const spendHint = useCallback(async () => {
        const isAutoApply = autoApplyNextHint.current;
        autoApplyNextHint.current = false;
        const useResult = await checkAndUseHint();
        if (!useResult.hintGranted && !isAutoApply) return null;
        setHintUsedFlash(true);
        setTimeout(() => setHintUsedFlash(false), 1200);
        return useResult;
    }, [checkAndUseHint]);

    // Records one hint step on the server; falls back to local counters when the call fails.
    // replacedPercent: the open clue's penalty, which the reveal of the same word replaces.
    const recordHintStep = useCallback(async (hintResult, hintType, applied, replacedPercent = 0) => {
        const backendMode = mode === 'practice' ? 'random' : mode;
        const fallbackPenalty = Math.round((1 - (hintType === 'clue' ? HINT_PENALTY_CLUE : hintResult.penaltyFactor)) * 100);
        try {
            const res = await api.recordHint(language, {
                hintType,
                hintWord: hintResult.hintWord,
                wordsToRemove: hintResult.wordsToRemove,
                removeFrom: hintResult.removeFrom,
                applied,
            }, backendMode);
            if (res.success) {
                setHintsUsed(res.hintsUsed);
                setTotalPenaltyPercent(res.totalPenaltyPercent);
                return { id: res.hintId, penaltyPercent: res.thisHintPenaltyPercent ?? fallbackPenalty };
            }
        } catch (e) {
            console.warn('Failed to record hint to server:', e);
        }
        if (!replacedPercent) setHintsUsed(prev => prev + 1);
        setTotalPenaltyPercent(prev => Math.round((1 - (1 - prev / 100) / (1 - replacedPercent / 100) * (1 - fallbackPenalty / 100)) * 100));
        return { id: Date.now(), penaltyPercent: fallbackPenalty };
    }, [language, mode]);

    // The clue: a sense of the next word that doesn't name it, or null when none exists.
    const fetchHintClue = useCallback(async (hintResult) => {
        let definition = null;
        try {
            const info = await api.getWordInfo(language, hintResult.hintWord);
            definition = pickClueDefinition(info?.definitions, [hintResult.hintWord, info?.lemma], language);
        } catch (e) {
            console.warn('Hint clue lookup failed:', e);
        }
        return { definition, lines: buildHintClueLines(hintResult, t) };
    }, [language, t]);

    // Step one of a hint: find the next word, spend a hint, show its clue (never the word itself)
    const handleHint = useCallback(async () => {
        if (!gameRef.current || gameProgress.isCompleted || hintLoading || currentHint) return;

        setHintLoading(true);
        setHintError(null);

        try {
            // Check if dictionary is loaded for client-side hint computation
            if (!gameRef.current.isDictionaryLoaded()) {
                setHintError(t('play.errors.dictionaryLoading'));
                setTimeout(() => setHintError(null), 3000);
                return;
            }

            // Get game state and dictionary for local hint computation
            const pathData = gameRef.current.getCurrentPath();
            const dictionary = gameRef.current.getDictionary();

            // Hints need the neighbour graph (word-list-only dictionaries have none)
            if (!dictionary || !dictionary.hasNeighborData) {
                // Old dictionary format without neighbors - clear cache and ask to refresh
                const cacheKey = `dictionaryCache_${language}`;
                localStorage.removeItem(cacheKey);
                setHintError(t('play.errors.dictionaryUpdate'));
                setTimeout(() => setHintError(null), 5000);
                return;
            }

            // Compute hint locally using client-side BFS
            const hintResult = findSmartHint(
                dictionary,
                pathData.startWord,
                pathData.targetWord,
                pathData.previousGuesses,
                pathData.nextGuesses,
                20, // maxDepth
                language
            );

            if (!hintResult.success) {
                // Hint calculation failed - DO NOT deduct anything
                // Record failure to server for analytics
                try {
                    await api.recordHint(language, { failed: true }, mode === 'practice' ? 'random' : mode);
                } catch (e) {
                    console.warn('Failed to record hint failure:', e);
                }
                setHintError(hintResult.error || t('play.errors.noHint'));
                setTimeout(() => setHintError(null), 5000);
                return;
            }

            // Same next word as the card already paid for: show it again instead of charging twice
            if (isSameHint(hintResult, lastHint)) {
                setCurrentHint(lastHint);
                return;
            }

            // Hint calculated successfully, NOW deduct the hint.
            if (!ensureHintAvailable()) return;
            const useResult = await spendHint();
            if (!useResult) return;

            const clue = await fetchHintClue(hintResult);
            const hintWord = upperWord(hintResult.hintWord);
            trackEvent('Hint Used', {
                mode,
                hint_source: useResult.source || 'purchased',
                stage: clue.definition ? 'clue' : 'reveal',
                has_definition: String(!!clue.definition),
            });

            if (!clue.definition) {
                // Nothing worth a clue: reveal the word right away at the usual price
                const rec = await recordHintStep(hintResult, hintResult.hintType, false);
                const instruction = buildHintInstruction(hintResult);
                const revealed = { ...hintResult, hintWord, id: rec.id, stage: 'reveal', clue, instruction, penaltyPercent: rec.penaltyPercent };
                setCurrentHint(revealed);
                setLastHint(revealed);
                setHintHistory(prev => [...prev, {
                    id: rec.id,
                    instruction,
                    hintType: hintResult.hintType,
                    hintWord,
                    wordsToRemove: hintResult.wordsToRemove,
                    removeFrom: hintResult.removeFrom,
                    penaltyPercent: rec.penaltyPercent,
                    applied: false,
                }]);
                return;
            }

            // The clue counts as used the moment it is shown
            const rec = await recordHintStep(hintResult, 'clue', true);
            const instruction = [t('game.hintClue.quote', { text: clue.definition }), ...clue.lines].join(' ');

            const clueHint = {
                ...hintResult,
                hintWord,
                id: rec.id,
                stage: 'clue',
                clue,
                instruction,
                penaltyPercent: rec.penaltyPercent,
                revealPenaltyPercent: Math.round((1 - hintResult.penaltyFactor) * 100),
            };
            setCurrentHint(clueHint);
            setLastHint(clueHint);
            setHintHistory(prev => [...prev, {
                id: rec.id,
                hintType: 'clue',
                instruction,
                penaltyPercent: rec.penaltyPercent,
                applied: true,
            }]);
        } catch (err) {
            console.error('Error getting hint:', err);
            setHintError(err.message || t('play.errors.hintFailed'));
            setTimeout(() => setHintError(null), 5000);
        } finally {
            setHintLoading(false);
        }
    }, [language, t, gameProgress.isCompleted, hintLoading, mode, currentHint, lastHint, ensureHintAvailable, spendHint, fetchHintClue, recordHintStep]);

    // Step two: spend another hint to turn the open clue into the word itself
    const handleRevealHint = useCallback(async () => {
        const clueHint = currentHint;
        if (!clueHint || clueHint.stage !== 'clue' || hintLoading) return;

        setHintLoading(true);
        try {
            if (!ensureHintAvailable()) return;
            const useResult = await spendHint();
            if (!useResult) return;

            trackEvent('Hint Used', { mode, hint_source: useResult.source || 'purchased', stage: 'reveal' });

            const rec = await recordHintStep(clueHint, clueHint.hintType, false, clueHint.penaltyPercent);
            const instruction = buildHintInstruction(clueHint);
            const revealed = { ...clueHint, id: rec.id, stage: 'reveal', instruction, penaltyPercent: rec.penaltyPercent };
            setCurrentHint(revealed);
            setLastHint(revealed);
            setHintHistory(prev => [...prev.filter(h => h.id !== clueHint.id), {
                id: rec.id,
                instruction,
                hintType: clueHint.hintType,
                hintWord: clueHint.hintWord,
                wordsToRemove: clueHint.wordsToRemove,
                removeFrom: clueHint.removeFrom,
                penaltyPercent: rec.penaltyPercent,
                applied: false,
            }]);
        } catch (err) {
            console.error('Error revealing hint:', err);
            setHintError(err.message || t('play.errors.hintFailed'));
            setTimeout(() => setHintError(null), 5000);
        } finally {
            setHintLoading(false);
        }
    }, [currentHint, hintLoading, mode, t, ensureHintAvailable, spendHint, recordHintStep]);

    // Refs so the ad/purchase callback always reaches the latest handlers and hint state
    const handleHintRef = useRef(handleHint);
    const handleRevealHintRef = useRef(handleRevealHint);
    const currentHintRef = useRef(currentHint);
    useEffect(() => {
        handleHintRef.current = handleHint;
        handleRevealHintRef.current = handleRevealHint;
        currentHintRef.current = currentHint;
    }, [handleHint, handleRevealHint, currentHint]);

    // Callback when hint is granted after watching ad or purchasing
    const handleHintUnlockGranted = useCallback(() => {
        // State was already updated by grantAdReward (hasAdHintAvailable = true).
        // Auto-apply so user doesn't need an extra click after watching an ad.
        autoApplyNextHint.current = true;
        // Defer slightly for iOS WebView to restore after native AdMob overlay closes.
        setTimeout(() => {
            if (currentHintRef.current?.stage === 'clue') handleRevealHintRef.current?.();
            else handleHintRef.current?.();
        }, 150);
    }, []);

    // Handle applying a hint
    const handleApplyHint = useCallback(async () => {
        if (!currentHint || currentHint.stage !== 'reveal' || !gameRef.current) return;

        const hintToApply = currentHint;

        // Apply the hint to the game state
        if (hintToApply.hintType === 'add') {
            gameRef.current.addHintWord(hintToApply.hintWord, hintToApply.direction);
        } else if (hintToApply.hintType === 'remove_add') {
            if (hintToApply.removeFrom === 'top') {
                gameRef.current.removeWordsFromTop(hintToApply.wordsToRemove);
            } else {
                gameRef.current.removeWordsFromBottom(hintToApply.wordsToRemove);
            }
            gameRef.current.addHintWord(hintToApply.hintWord, hintToApply.direction);
        }

        // Clear hint immediately (don't wait for API)
        setCurrentHint(null);

        // Update history to mark as applied
        setHintHistory(prev => prev.map(h =>
            h.id === hintToApply.id ? { ...h, applied: true } : h
        ));

        // Mark hint as applied in backend (retry once on failure)
        api.applyHint(language, hintToApply.id).catch(err => {
            console.error('Error applying hint, retrying:', err);
            setTimeout(() => {
                api.applyHint(language, hintToApply.id).catch(err2 => {
                    console.error('Hint apply retry failed:', err2);
                });
            }, 2000);
        });

        // Save game state after hint apply
        saveGameStateToServer(false);
    }, [language, currentHint, saveGameStateToServer]);

    // Dismiss hint without applying
    const handleDismissHint = useCallback(() => {
        setCurrentHint(null);
    }, []);

    // Clear hint view when user submits a word
    const handleWordSubmitted = useCallback(() => {
        setCurrentHint(null);
        // Save game state after each word entry
        saveGameStateToServer(false);
    }, [saveGameStateToServer]);

    // Handle retry - reload the same game
    const handleRetry = useCallback(() => {
        // Reset the game state by re-triggering a load with the same game
        if (gameData) {
            // Reset local state
            setGameProgress(prev => ({
                ...prev,
                isCompleted: false,
                bestSteps: null,
            }));

            // Reset hints
            setHintsUsed(0);
            setCurrentHint(null);
            setLastHint(null);
            setHintHistory([]);
            setTotalPenaltyPercent(0);
            setHintError(null);

            // Clear saved hints for this game
            clearHintState(gameData.startWord, gameData.targetWord);

            // Clear saved game state for this game
            clearGameStateFromLocal(gameData.startWord, gameData.targetWord);
            setSavedGameState(null);

            // Reset hint status in monetization context
            resetGameHintStatus();

            // Reset timer and achievements for retry
            gameStartTimeRef.current = Date.now();
            setNewlyEarnedAchievements([]);

            // Increment retry count to force Game component remount
            setRetryCount(prev => prev + 1);
        }
    }, [gameData, clearHintState, clearGameStateFromLocal, resetGameHintStatus]);

    // Get current history game data (for rendering completed game from history)
    const currentHistoryGame = historyIndex >= 0 && historyIndex < gameHistory.length
        ? gameHistory[historyIndex] : null;

    // Navigate back through game history
    const handleNavigateBack = useCallback(async () => {
        // For ranked/challenge: use existing handlePreviousGame
        if ((mode === 'ranked' || mode === 'challenge') && (ladderData || challengeData)) {
            handlePreviousGame();
            return;
        }

        // For practice/expert: browse game history
        if (historyIndex === -1) {
            // Currently on active game — load history if needed
            if (gameHistory.length === 0 && !historyLoading) {
                setHistoryLoading(true);
                try {
                    const backendMode = mode === 'practice' ? 'random' : 'expert';
                    const response = await api.fetchGameHistory(language, backendMode, 1);
                    if (response.success && response.games.length > 0) {
                        setGameHistory(response.games);
                        setHistoryPage(1);
                        setHistoryHasMore(response.pagination.hasMore);
                        setHistoryIndex(0);
                        setHasPastGames(true);
                    }
                } catch (err) {
                    console.error('Error loading game history:', err);
                } finally {
                    setHistoryLoading(false);
                }
                return;
            }
            // History already loaded — go to first history item
            if (gameHistory.length > 0) {
                setHistoryIndex(0);
            }
            return;
        }

        // Already in history — go further back
        const nextHistIdx = historyIndex + 1;
        if (nextHistIdx < gameHistory.length) {
            setHistoryIndex(nextHistIdx);
        } else if (historyHasMore && !historyLoading) {
            // Load more history
            setHistoryLoading(true);
            try {
                const backendMode = mode === 'practice' ? 'random' : 'expert';
                const nextPage = historyPage + 1;
                const response = await api.fetchGameHistory(language, backendMode, nextPage);
                if (response.success && response.games.length > 0) {
                    setGameHistory(prev => [...prev, ...response.games]);
                    setHistoryPage(nextPage);
                    setHistoryHasMore(response.pagination.hasMore);
                    setHistoryIndex(nextHistIdx);
                }
            } catch (err) {
                console.error('Error loading more history:', err);
            } finally {
                setHistoryLoading(false);
            }
        }
    }, [mode, ladderData, challengeData, handlePreviousGame, historyIndex, gameHistory, historyLoading, historyHasMore, historyPage, language]);

    // Navigate forward through game history
    const handleNavigateForward = useCallback(() => {
        if (mode === 'onboarding') return; // the tutorial advances itself after each win
        if (mode === 'shared') {
            clearSharedLink();
            setSavedGameState(null);
            setLoading(true);
            setMode('ranked');
            return;
        }
        // For ranked/challenge: use existing handleNextGame flow
        if ((mode === 'ranked' || mode === 'challenge') && (ladderData || challengeData)) {
            handleNextGame();
            return;
        }

        // For practice/expert: navigate forward in history toward current game
        if (historyIndex > 0) {
            setHistoryIndex(historyIndex - 1);
        } else if (historyIndex === 0) {
            // Return to current active game
            setHistoryIndex(-1);
        } else if (historyIndex === -1) {
            // On active completed game — get a new game
            handleNextGame();
        }
    }, [mode, ladderData, challengeData, handleNextGame, historyIndex, clearSharedLink]);

    // Retry a game from history view
    const handleRetryFromHistory = useCallback(() => {
        if (!currentHistoryGame) return;
        // Set game data from history and exit history mode
        setGameData({
            startWord: currentHistoryGame.startWord,
            targetWord: currentHistoryGame.targetWord,
            title: `${currentHistoryGame.startWord} → ${currentHistoryGame.targetWord}`,
            minSteps: currentHistoryGame.minSteps,
            maxStars: 3,
        });
        setGameProgress(prev => ({
            ...prev,
            isCompleted: false,
            bestSteps: null,
        }));
        setHistoryIndex(-1);
        setSavedGameState(null);
        setHintsUsed(0);
        setCurrentHint(null);
        setLastHint(null);
        setHintHistory([]);
        setTotalPenaltyPercent(0);
        setHintError(null);
        resetGameHintStatus();
        gameStartTimeRef.current = Date.now();
        setNewlyEarnedAchievements([]);
        setRetryCount(prev => prev + 1);
    }, [currentHistoryGame, resetGameHintStatus]);

    // Clear history when switching modes
    const clearHistory = useCallback(() => {
        setGameHistory([]);
        setHistoryIndex(-1);
        setHistoryPage(1);
        setHistoryHasMore(false);
        setHasPastGames(false);
    }, []);

    // Track when past games exist for practice/expert Back button
    useEffect(() => {
        if ((mode === 'practice' || mode === 'expert') && gameProgress.isCompleted) {
            setHasPastGames(true);
        }
    }, [mode, gameProgress.isCompleted]);

    // Callback when hint is used in Game component
    const handleHintUsed = useCallback((hintWord, direction) => {
        // Hint applied - no-op for now, could track analytics
    }, []);

    // Server display strings come with a stable code: translate by code, fall back to the server text.
    const tOr = (key, fallback) => { const s = t(key); return s === key ? fallback : s; };
    // Where to go after the last daily puzzle: weekly challenge (while unfinished), expert, practice.
    useEffect(() => {
        if (mode !== 'ranked' || !navigation.ladderComplete) return undefined;
        let cancelled = false;
        api.fetchChallenge(language).then(res => {
            if (cancelled || !res?.success) return;
            setChallengePeek({ done: res.progress?.completedCount || 0, total: res.challenge?.totalGames || 0 });
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [mode, navigation.ladderComplete, language]);

    const pickNextMode = (target, context) => {
        trackEvent('Next Mode Picked', { from: context, to: target });
        handleModeChange(target);
        if (target === 'practice') setDifficulty(gameProgress.levelNumber || 1);
    };

    const renderNextModes = (context) => {
        const tiles = [];
        if (mode !== 'challenge' && (!challengePeek || challengePeek.done < challengePeek.total)) {
            tiles.push({ key: 'challenge', icon: faStar, sub: challengePeek
                ? t('play.celebration.challengeGames', { done: challengePeek.done, total: challengePeek.total })
                : t('play.modes.desc.challenge') });
        }
        tiles.push({ key: 'expert', icon: faFire, sub: t('play.modes.desc.expert') });
        tiles.push({ key: 'practice', icon: faDumbbell, sub: t('play.modes.desc.practice') });
        return (
            <div className="next-modes" data-count={tiles.length}>
                {tiles.map(tile => (
                    <button key={tile.key} type="button" className={`next-mode-tile next-mode-tile--${tile.key}`}
                        onClick={() => pickNextMode(tile.key, context)}>
                        <FontAwesomeIcon icon={tile.icon} fixedWidth />
                        <span className="next-mode-label">{t(`play.modes.${tile.key}`)}</span>
                        <span className="next-mode-sub">{tile.sub}</span>
                    </button>
                ))}
            </div>
        );
    };

    // Returning phone players (wins on 2+ days) already get the app link on every victory card.
    const returningPhonePlayer = () => {
        try { return JSON.parse(localStorage.getItem('wl_win_days') || '[]').length >= 2; } catch { return false; }
    };
    const appStoreUrl = !isNative() && !returningPhonePlayer() ? storeUrlForThisDevice() : null;
    const renderAppNudge = (context) => {
        if (!appStoreUrl || appNudgeDismissed) return null;
        const dismiss = () => {
            setAppNudgeDismissed(true);
            try { localStorage.setItem(APP_NUDGE_KEY, String(Date.now())); } catch { /* ignore */ }
        };
        return (
            <div className="app-nudge">
                <FontAwesomeIcon icon={faMobileScreen} className="app-nudge-icon" />
                <p className="app-nudge-text">{t('feedback.getApp')}</p>
                <a className="app-nudge-link" href={appStoreUrl} target="_blank" rel="noopener noreferrer"
                    onClick={() => { trackEvent('Get App Clicked', { context }); dismiss(); }}>
                    {t('nav.getApp')}
                </a>
                <button type="button" className="app-nudge-dismiss" onClick={dismiss} aria-label={t('common.close')}>&#x2715;</button>
            </div>
        );
    };

    const challengeText = (field) => tOr(`play.challenge.types.${challengeData?.type}.${field}`,
        field === 'name' ? challengeData?.displayName : challengeData?.description);
    const tierName = (code, fallback) => tOr(`rank.tiers.${code}`, fallback);

    // ── Guided onboarding: gate (Start/Skip) and handoff screens ──
    const completeOnboarding = (status) => {
        updatePreference('onboardingStep', status);   // 'done' | 'skipped'
        updatePreference('onboardingCompleted', true);
        updatePreference('tutorialSeen', true);        // existing-user signal
        setOnboardingStep('done');
        setGameData(null);   // show the loader, not the tutorial board, until the ladder arrives
        setLoading(true);
        setMode('ranked');
    };
    // Leaving the welcome screen makes the (possibly device-detected) language an explicit choice.
    const confirmLanguage = () => {
        const chosen = !!localStorage.getItem('language');
        setLanguage(language);
        trackEvent('onboarding_language', { language, source: chosen ? 'picked' : 'device' });
    };
    if (mode === 'onboarding' && onboardingStep === 0) {
        return (
            <div className="game-page">
                <div className="onboarding-screen">
                    <div className="onboarding-card">
                        <div className="onboarding-demo" aria-hidden="true">
                            {(ONBOARDING_DEMO[language] || ONBOARDING_DEMO.en).map(([word, hi], i) => (
                                <React.Fragment key={word}>
                                    {i > 0 && <div className="od-step"><i className="fa fa-arrow-down"></i></div>}
                                    <div className="od-word">
                                        {word.split('').map((ch, j) => <span key={j} className={j === hi ? 'od-hi' : undefined}>{ch}</span>)}
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                        <h2>{t('play.onboarding.welcomeTitle')}</h2>
                        <p className="onboarding-sub">{t('play.onboarding.welcomeLead')}</p>
                        {SUPPORTED_LANGUAGES.length > 1 && (
                            <div className="onboarding-langs" role="group" aria-label={t('play.onboarding.chooseLanguage')}>
                                {SUPPORTED_LANGUAGES.map(code => (
                                    <button key={code} type="button" aria-pressed={code === language}
                                        className={`onboarding-lang${code === language ? ' active' : ''}`}
                                        onClick={() => setLanguage(code)}>
                                        <span aria-hidden="true">{LANGUAGE_BY_CODE[code]?.flag}</span> {LANGUAGE_NAMES[code]}
                                    </button>
                                ))}
                            </div>
                        )}
                        <button className="onboarding-primary-btn" onClick={() => { confirmLanguage(); setLoading(true); setOnboardingStep(1); }}>{t('play.onboarding.showMe')}</button>
                        <button className="onboarding-skip-btn" onClick={() => { confirmLanguage(); trackEvent('onboarding_skipped', {}); completeOnboarding('skipped'); }}>{t('play.onboarding.alreadyKnow')}</button>
                    </div>
                </div>
            </div>
        );
    }
    if (mode === 'onboarding' && onboardingStep === 3) {
        return (
            <div className="game-page">
                <div className="onboarding-screen">
                    <div className="onboarding-card">
                        <div className="onboarding-check" aria-hidden="true"><i className="fa fa-check"></i></div>
                        <h2>{t('play.onboarding.doneTitle')}</h2>
                        <p className="onboarding-sub">{t.rich('play.onboarding.doneLead', { b: <strong /> })}</p>
                        <button className="onboarding-primary-btn" onClick={() => completeOnboarding('done')}>{t('play.onboarding.enterLadder')}</button>
                    </div>
                </div>
            </div>
        );
    }

    if (loading && !gameData) {
        return (
            <div className="game-page">
                <div className="loading" role="status" aria-live="polite">
                    <div className="loading-spinner"></div>
                    <p>{t('play.loading.game')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="game-page">
                <div className="error" role="alert">{error}</div>
                <button onClick={() => loadGame()}>{t('common.retry')}</button>
            </div>
        );
    }

    if (!gameData) {
        return (
            <div className="game-page">
                <div className="no-game">{t('play.noGame')}</div>
            </div>
        );
    }

    return (
        <div className="game-page-wrapper">
            <PageHead>
                <title>{t('play.seo.title')}</title>
                <meta name="description" content={t('play.seo.description')} />
                <meta property="og:title" content={t('play.seo.title')} />
            </PageHead>
            {/* Email verification banner */}
            {verificationBanner && (
                <div className={`verification-banner ${verificationBanner.type}`}>
                    <i className={`fa ${verificationBanner.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
                    <span>{t(verificationBanner.messageKey)}</span>
                    <button onClick={() => setVerificationBanner(null)} className="banner-dismiss" aria-label={t('common.close')}>
                        <i className="fa fa-times"></i>
                    </button>
                </div>
            )}

            {/* Loading overlay for game transitions */}
            {loading && gameData && (
                <div className="loading-overlay" role="status" aria-live="polite">
                    <div className="loading-spinner"></div>
                    <p>{modeSwitchRef.current ? t('play.loading.switchingMode') : t('play.loading.nextGame')}</p>
                </div>
            )}

            {/* Login Modal — yields to a pending streak milestone / recovery modal
                so they don't stack on a win; it re-appears once those close (B9). */}
            {showLoginPrompt && !(pendingMilestones && pendingMilestones.length > 0) && !showRecoveryModal && (
                <LoginModal
                    show={showLoginPrompt}
                    onClose={dismissLoginPrompt}
                    message={loginPromptMessage}
                    startInSignup={loginPromptSignup}
                />
            )}

            {/* Hint Unlock Modal */}
            <HintUnlockModal onHintGranted={handleHintUnlockGranted} />

            {/* Purchase Modal */}
            <PurchaseModal />

            {/* Streak Milestone Modal */}
            {pendingMilestones && pendingMilestones.length > 0 && (
                <StreakMilestoneModal
                    milestones={pendingMilestones}
                    onClose={() => setPendingMilestones(null)}
                />
            )}

            {/* Streak Recovery Modal */}
            {showRecoveryModal && streakData?.recovery && (
                <StreakRecoveryModal
                    recovery={streakData.recovery}
                    onClose={(recovered) => {
                        setShowRecoveryModal(false);
                        if (recovered) {
                            refreshStreak();
                        } else if (streakData?.recovery?.streakLostAt) {
                            updatePreference('recoveryDismissedFor', streakData.recovery.streakLostAt);
                        }
                    }}
                    onOpenStore={() => openPurchaseModal()}
                />
            )}

            {/* Hint Earned Toast */}
            {hintEarnedToast && (
                <div className="hint-earned-toast">
                    <span role="img" aria-label={t('play.hint.earnedIcon')}>&#x1F4A1;</span>
                    {' '}{t('play.hint.earned', { count: hintEarnedToast.hints })}
                </div>
            )}

            {/* Rank Progression Modal */}
            <RankProgressionModal
                show={showRankPromotion}
                ratingUpdate={rankPromotionData}
                onDismiss={handleRankPromotionDismiss}
            />

            {/* Mode Selector */}
            <div className="mode-selector" ref={modeSelectorRef}>
                <button
                    className={`mode-button ${mode === 'ranked' ? 'active' : ''}`}
                    onClick={() => handleModeChange('ranked')}
                    disabled={loading}
                >
                    <FontAwesomeIcon icon={faTrophy} fixedWidth /><span>{t('play.modes.ranked')}</span>
                </button>
                <button
                    className={`mode-button ${mode === 'practice' ? 'active' : ''}`}
                    onClick={() => handleModeChange('practice')}
                    disabled={loading}
                >
                    <FontAwesomeIcon icon={faDumbbell} fixedWidth /><span>{t('play.modes.practice')}</span>
                </button>
                <button
                    className={`mode-button expert-mode ${mode === 'expert' ? 'active' : ''}`}
                    onClick={() => handleModeChange('expert')}
                    disabled={loading}
                >
                    <FontAwesomeIcon icon={faFire} fixedWidth /><span>{t('play.modes.expert')}</span>
                </button>
                <button
                    className={`mode-button ${mode === 'challenge' ? 'active' : ''}`}
                    onClick={() => handleModeChange('challenge')}
                    disabled={loading}
                >
                    <FontAwesomeIcon icon={faStar} fixedWidth /><span>{t('play.modes.challenge')}</span>
                </button>
            </div>
            {['ranked', 'practice', 'expert', 'challenge'].includes(mode) && (
                <p className="mode-caption" aria-live="polite">{t(`play.modes.desc.${mode}`)}</p>
            )}

            {/* Row 2: Compact Rank + Level Progress */}
            <div className={`info-bar${(mode === 'ranked' || mode === 'challenge') && gameProgress.totalGames > 0 ? ' info-bar--spread' : ''}`}>
                <RankProgressBar
                    rankProgress={rankProgress}
                    compact={true}
                    showPracticeNote={false}
                />
                {(mode === 'ranked' || mode === 'challenge') && gameProgress.totalGames > 0 && (
                    <div className="level-progress-inline">
                        <span className="level-label-inline">{t('play.levelShort', { level: gameProgress.levelNumber })}</span>
                        <span className="level-count-inline">{gameProgress.wonGames}/{gameProgress.totalGames}</span>
                        <div className="level-bar-inline">
                            <div
                                className="level-bar-fill-inline"
                                style={{ width: `${gameProgress.progress}%` }}
                            />
                        </div>
                    </div>
                )}
                {/* B20: practice personal bests */}
                {mode === 'practice' && practiceStats && practiceStats.completed > 0 && (
                    <div className="practice-stats-inline">
                        <span className="practice-stat" title={t('play.practice.completedTitle')}>
                            <span role="img" aria-label={t('play.practice.completedIcon')}>🧩</span> {practiceStats.completed}
                        </span>
                        {practiceStats.bestSteps != null && (
                            <span className="practice-stat" title={t('play.practice.bestTitle')}>
                                <span role="img" aria-label={t('play.practice.bestIcon')}>🏆</span> {t('common.count.steps', { count: practiceStats.bestSteps })}
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* Backend Status */}
            {!backendConnected && (
                <div className="api-status warning" role="alert">
                    <i className="fa fa-exclamation-triangle"></i>
                    {t('play.demoMode')}
                </div>
            )}

            {/* Inactivity decay notification */}
            {decayUpdate && (
                <div className="decay-notification" role="alert">
                    <span>
                        <i className="fa fa-exclamation-triangle"></i>
                        {' '}{t('play.decay.message', { count: decayUpdate.starsLost, days: decayUpdate.daysInactive })}
                        {decayUpdate.rankChanged && ` ${t('play.decay.rankChange', { from: tierName(decayUpdate.oldTier, decayUpdate.oldTierName), to: tierName(decayUpdate.rankTier, decayUpdate.rankName) })}`}
                    </span>
                    <button className="decay-dismiss" onClick={() => setDecayUpdate(null)} aria-label={t('common.close')}>
                        <i className="fa fa-times"></i>
                    </button>
                </div>
            )}

            {/* Challenge Banner - compact */}
            {mode === 'challenge' && challengeData && (
                <div className="challenge-banner-compact">
                    <div className="challenge-banner-scroll" ref={challengeBannerRef}>
                        <i className={`fa fa-${challengeData.icon}`}></i>
                        <span className="challenge-compact-name">{challengeText('name')}</span>
                        <span className="challenge-compact-sep">·</span>
                        <span className="challenge-compact-desc">{challengeText('description')}</span>
                        <span className="challenge-compact-sep">·</span>
                        <span className="challenge-compact-timer">{t('play.challenge.ends', { date: new Date(challengeData.weekEnd + 'T23:59:59').toLocaleDateString(locale) })}</span>
                    </div>
                </div>
            )}

            {mode === 'shared' && sharedChallenge && (
                <div className="challenge-banner-compact shared-banner" role="note">
                    <span className="shared-banner-text">
                        <span aria-hidden="true">{sharedChallenge.own ? '🔗' : '🏁'}</span>{' '}
                        {sharedChallenge.own
                            ? t('game.shared.bannerOwn')
                            : t(`game.shared.${sharedChallenge.playerSteps <= sharedChallenge.minSteps ? 'bannerPerfect' : 'banner'}${sharedChallenge.sharerName ? '' : 'Anon'}`,
                                { name: sharedChallenge.sharerName, count: sharedChallenge.playerSteps })}
                    </span>
                </div>
            )}

            {/* Practice Mode Difficulty - compact */}
            {mode === 'practice' && (
                <div className="difficulty-selector-compact">
                    <label>{t('play.practice.difficultyLabel')}</label>
                    <select
                        value={difficulty}
                        onChange={(e) => {
                            setDifficulty(parseInt(e.target.value));
                            setLoading(true);
                        }}
                    >
                        {[...Array(10)].map((_, i) => (
                            <option key={i + 1} value={i + 1}>
                                {t('play.practice.levelOption', { level: i + 1, difficulty: t(`common.difficulty.${i < 3 ? 'easy' : i < 6 ? 'medium' : 'hard'}`) })}
                            </option>
                        ))}
                    </select>
                </div>
            )}

{/* Custom Mode Inputs - hidden but functionality preserved
            {mode === 'custom' && (
                <div className="custom-inputs">
                    <input
                        type="text"
                        placeholder="Start word"
                        value={customWords.start}
                        onChange={(e) => setCustomWords(prev => ({ ...prev, start: foldLetters(upperWord(e.target.value), language) }))}
                    />
                    <span className="arrow">→</span>
                    <input
                        type="text"
                        placeholder="Target word"
                        value={customWords.target}
                        onChange={(e) => setCustomWords(prev => ({ ...prev, target: foldLetters(upperWord(e.target.value), language) }))}
                    />
                    <button
                        className="start-button"
                        onClick={() => loadGame()}
                        disabled={!customWords.start || !customWords.target}
                    >
                        Start
                    </button>
                </div>
            )}
            */}

            {/* Solution Path - shown for skipped games */}
            {gameProgress.isCompleted && gameProgress.bestSteps === 999 && ladderData?.games?.[currentGameIndex]?.solutionPath && (
                <div className="skipped-solution">
                    <div className="skipped-solution-header">
                        <i className="fa fa-eye"></i> {t('play.solutionPath')}
                    </div>
                    <div className="skipped-solution-path">
                        {ladderData.games[currentGameIndex].solutionPath.map((word, i) => (
                            <span key={i} className="solution-word">
                                {word}
                                {i < ladderData.games[currentGameIndex].solutionPath.length - 1 && (
                                    <span className="solution-arrow"> → </span>
                                )}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Current hint: first the clue (meaning + shape), then the word once revealed */}
            {currentHint && (
                <div className={`hint-instruction${currentHint.stage === 'clue' ? ' hint-clue' : ''}`} role="status" aria-live="polite">
                    <div className="hint-instruction-header">
                        <i className="fa fa-lightbulb"></i>
                        <span>{t(currentHint.stage === 'clue' ? 'play.hint.clueTitle' : 'play.hint.title')}</span>
                        <span className="hint-penalty-badge">{(-currentHint.penaltyPercent / 100).toLocaleString(locale, { style: 'percent' })}</span>
                    </div>
                    <div className="hint-instruction-body">
                        {currentHint.stage === 'reveal' && (
                            <div className="hint-reveal-word">{buildHintInstruction(currentHint, t)}</div>
                        )}
                        {currentHint.clue?.definition && (
                            <div className="hint-clue-definition">{t('game.hintClue.quote', { text: currentHint.clue.definition })}</div>
                        )}
                        {currentHint.stage === 'clue' && currentHint.clue?.lines.map((line, i) => (
                            <div key={i} className="hint-clue-shape">{line}</div>
                        ))}
                    </div>
                    <div className="hint-instruction-actions">
                        {currentHint.stage === 'clue' ? (
                            <>
                                <button
                                    className="apply-hint-button reveal-hint-button"
                                    onClick={handleRevealHint}
                                    disabled={hintLoading}
                                >
                                    <i className="fa fa-eye"></i> {t('play.hint.reveal')}
                                    <span className="hint-cost">{(-currentHint.revealPenaltyPercent / 100).toLocaleString(locale, { style: 'percent' })}</span>
                                </button>
                                <button
                                    className="dismiss-hint-button"
                                    onClick={handleDismissHint}
                                >
                                    <i className="fa fa-check"></i> {t('play.hint.gotIt')}
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    className="apply-hint-button"
                                    onClick={handleApplyHint}
                                >
                                    <i className="fa fa-check"></i> {t('play.hint.apply')}
                                </button>
                                <button
                                    className="dismiss-hint-button"
                                    onClick={handleDismissHint}
                                >
                                    <i className="fa fa-times"></i> {t('play.hint.dismiss')}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Hint History - Collapsible */}
            {hintHistory.length > 0 && hintHistoryExpanded && !currentHint && (
                <div className="hint-history">
                    {hintHistory.map((hint, index) => (
                        <div
                            key={hint.id}
                            className={`hint-history-item ${hint.applied ? 'applied' : 'pending'}`}
                        >
                            <span className="hint-number">#{index + 1}</span>
                            <span className="hint-instruction-text">{hint.hintWord ? buildHintInstruction(hint, t) : hint.instruction}</span>
                            <span className="hint-penalty">{(-hint.penaltyPercent / 100).toLocaleString(locale, { style: 'percent' })}</span>
                            {hint.applied && <i className="fa fa-check applied-icon"></i>}
                        </div>
                    ))}
                </div>
            )}

            {/* Hint Error Display */}
            {hintError && (
                <div className="hint-error" role="alert">
                    <i className="fa fa-exclamation-circle"></i> {hintError}
                </div>
            )}

            {/* Game Component */}
            <Game
                ref={gameRef}
                key={`${currentHistoryGame ? `hist-${historyIndex}-` : ''}${gameData.startWord}-${gameData.targetWord}-${language}-${retryCount}`}
                initialData={currentHistoryGame ? {
                    startWord: currentHistoryGame.startWord,
                    targetWord: currentHistoryGame.targetWord,
                    title: `${currentHistoryGame.startWord} → ${currentHistoryGame.targetWord}`,
                    minSteps: currentHistoryGame.minSteps,
                } : gameData}
                language={mode === 'shared' && sharedChallenge ? sharedChallenge.language : language}
                minSteps={currentHistoryGame ? currentHistoryGame.minSteps : gameData.minSteps}
                mode={mode}
                enableFirstMoveGuide={mode === 'onboarding' || (mode === 'shared' && freshVisitor)}
                coachingMode={mode === 'onboarding' && onboardingStep === 1}
                onGameWon={currentHistoryGame ? undefined : handleGameWon}
                onHintUsed={currentHistoryGame ? undefined : handleHintUsed}
                onNextGame={handleNavigateForward}
                onRetry={currentHistoryGame ? handleRetryFromHistory : handleRetry}
                onPreviousGame={handleNavigateBack}
                onWordSubmitted={currentHistoryGame ? undefined : handleWordSubmitted}
                onWrongWay={currentHistoryGame ? undefined : () => setShowWrongWayModal(true)}
                showDistanceIndicator={preferences.showDistanceIndicator !== false}
                onExpertDifficulty={currentHistoryGame ? undefined : handleExpertDifficulty}
                canGoNext={currentHistoryGame ? (historyIndex > 0 || historyIndex === 0) : (navigation.canGoNext && !navigation.ladderComplete)}
                canGoBack={currentHistoryGame
                    ? (historyIndex < gameHistory.length - 1 || historyHasMore)
                    : (mode === 'ranked' || mode === 'challenge'
                        ? navigation.canGoBack
                        : (gameHistory.length > 0 || gameProgress.isCompleted))}
                historyData={currentHistoryGame || (() => {
                    // For ranked/challenge, provide userPath from ladder data for completed games
                    if (gameProgress.isCompleted && !currentHistoryGame) {
                        const gamesArr = mode === 'ranked' && ladderData ? ladderData.games :
                            mode === 'challenge' && challengeData ? challengeData.games : null;
                        if (gamesArr && gamesArr[currentGameIndex]?.userPath) {
                            return { userPath: gamesArr[currentGameIndex].userPath };
                        }
                        if ((mode === 'practice' || mode === 'expert') && completedUserPath) {
                            return { userPath: completedUserPath };
                        }
                    }
                    return null;
                })()}
                initialSavedState={currentHistoryGame ? null : savedGameState}
                initialElapsedSeconds={currentHistoryGame ? 0 : (savedGameState?.elapsedSeconds || 0)}
                isCompleted={currentHistoryGame ? true : gameProgress.isCompleted}
                suppressVictoryOverlayOnLoad={navigation.ladderComplete === true}
                finalActions={!currentHistoryGame && navigation.ladderComplete && (mode === 'ranked' || mode === 'challenge')
                    ? renderNextModes(`victory_${mode}`) : null}
                streak={currentHistoryGame || mode === 'shared' ? 0 : streakData?.currentStreak}
                achievements={currentHistoryGame ? [] : newlyEarnedAchievements}
                ratingUpdate={currentHistoryGame ? null : ratingUpdate}
                rankProgressUpdate={currentHistoryGame ? null : rankProgressUpdate}
                maxStars={currentHistoryGame ? 3 : (gameData.maxStars ?? 3)}
                shareInfo={{
                    mode,
                    levelNumber: mode === 'shared' ? null : gameProgress.levelNumber,
                    totalGames: challengeData?.totalGames || ladderData?.totalGames || gameProgress.totalGames,
                    ladderDate: mode === 'ranked' ? ladderData?.date : null,
                }}
                victoryExtra={mode === 'shared' && sharedOutcome && !sharedChallenge?.own ? (
                    <p className={`victory-versus victory-versus--${sharedOutcome.outcome}`}>
                        {t(`game.shared.${sharedOutcome.outcome}`, { count: sharedOutcome.steps, other: sharedOutcome.sharerSteps })}
                    </p>
                ) : null}
                nextLabel={mode === 'shared' ? t('game.shared.next') : null}
                hintEarned={currentHistoryGame ? null : hintEarnedToast}
                completedSteps={currentHistoryGame ? null : gameProgress.bestSteps}
                ladderPosition={
                    currentHistoryGame ? null
                        : mode === 'ranked' && ladderData ? { done: gameProgress.wonGames, total: ladderData.totalGames }
                        : mode === 'challenge' && challengeData ? { done: gameProgress.wonGames, total: challengeData.totalGames }
                        : null
                }
                feedbackSlot={victoryFeedback && !currentHistoryGame ? (
                    <FeedbackPrompt compact context="victory" mode={mode} difficulty={difficulty} lang={language} gameId={currentGameIdRef.current}
                        gamesCompleted={parseInt(localStorage.getItem('wl_wins_total') || '0', 10) || null}
                        onDone={() => setVictoryFeedback(false)} />
                ) : null}
                challengeConstraints={mode === 'challenge' && challengeData ? challengeData.constraints : null}
                actionBar={
                    !gameProgress.isCompleted && !currentHistoryGame && backendConnected ? (
                        <div className="game-action-row game-action-row-embedded">
                            {mode === 'ranked' && skipAvailable && (
                                <button
                                    className="skip-button skip-button-sm"
                                    onClick={handleSkipGame}
                                    title={t('play.skipTitle')}
                                >
                                    <i className="fa fa-forward"></i> {t('common.skip')}
                                </button>
                            )}
                            <button
                                className="hint-button hint-forward hint-button-sm"
                                onClick={() => handleHint()}
                                disabled={hintLoading || gameProgress.isCompleted || currentHint}
                                title={t('play.hint.buttonTitle')}
                            >
                                {hintLoading ? (
                                    <span className="hint-loading">...</span>
                                ) : (
                                    <><i className="fa fa-lightbulb"></i> {t('play.hint.button')}</>
                                )}
                            </button>
                            <div className={`hint-balance-indicator hint-balance-sm ${monetizationLoading ? 'hint-balance-loading' : 'hint-balance-ready'}`}>
                                {freeHintsRemaining > 0 && (
                                    <span className="free-hints hint-badge-tooltip">
                                        <i className="fa fa-gift"></i> {freeHintsRemaining}
                                        <span className="hint-tooltip">{t('play.hint.freeTooltip')}</span>
                                    </span>
                                )}
                                {hasAdHintAvailable && isNativeMobile() && (
                                    <span className="ad-hints hint-badge-tooltip">
                                        <i className="fa fa-play-circle"></i> 1
                                        <span className="hint-tooltip">{t('play.hint.adTooltip')}</span>
                                    </span>
                                )}
                                <span
                                    className={`purchased-hints hint-badge-tooltip${purchasesEnabled ? ' hint-badge-clickable' : ''}${hintUsedFlash ? ' hint-used-flash' : ''}`}
                                    onClick={purchasesEnabled ? openPurchaseModal : undefined}
                                    onKeyDown={purchasesEnabled ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPurchaseModal(); } } : undefined}
                                    role={purchasesEnabled ? 'button' : undefined}
                                    tabIndex={purchasesEnabled ? 0 : undefined}
                                    aria-label={t(purchasesEnabled ? 'play.hint.balanceAriaBuy' : 'play.hint.balanceAria', { count: hintBalance })}
                                >
                                    <i className="fa fa-coins"></i> {hintBalance}
                                    {hintUsedFlash && <span className="hint-used-indicator">−1</span>}
                                    <span className="hint-tooltip">{t(purchasesEnabled ? 'play.hint.balanceTooltipBuy' : 'play.hint.balanceTooltip', { count: hintBalance })}</span>
                                </span>
                            </div>
                            {hintsUsed > 0 && (
                                <button
                                    className={`hints-used-badge ${hintHistoryExpanded ? 'expanded' : ''}`}
                                    onClick={() => setHintHistoryExpanded(!hintHistoryExpanded)}
                                    title={t('play.hint.historyToggle')}
                                >
                                    {t('play.hint.used', { count: hintsUsed, penalty: totalPenaltyPercent })}
                                    <i className={`fa fa-chevron-${hintHistoryExpanded ? 'up' : 'down'}`}></i>
                                </button>
                            )}
                        </div>
                    ) : null
                }
            />

            {/* Completed Game Stats */}
            {(() => {
                // Determine stats source: history game or current game from ranked/challenge
                const isHistView = !!currentHistoryGame;
                const isRankedCompleted = !isHistView && gameProgress.isCompleted && (mode === 'ranked' || mode === 'challenge');
                const rankedGame = isRankedCompleted ? (
                    mode === 'ranked' && ladderData ? ladderData.games[currentGameIndex] :
                    mode === 'challenge' && challengeData ? challengeData.games[currentGameIndex] : null
                ) : null;

                const showStats = isHistView || (gameProgress.isCompleted && gameProgress.bestSteps != null && mode !== 'onboarding');
                if (!showStats) return null;

                const steps = isHistView ? currentHistoryGame.bestSteps : gameProgress.bestSteps;
                const minS = isHistView ? currentHistoryGame.minSteps : gameData.minSteps;
                const elapsed = isHistView ? currentHistoryGame.elapsedSeconds : rankedGame?.elapsedSeconds;
                const hints = isHistView ? currentHistoryGame.hintsUsed : rankedGame?.hintsUsed;
                const stars = isHistView ? currentHistoryGame.stars : (steps && minS ? (steps <= minS ? 3 : steps <= minS + 1 ? 2 : 1) : null);

                const formatTime = (secs) => {
                    if (!secs && secs !== 0) return null;
                    const m = Math.floor(secs / 60);
                    const s = Math.round(secs % 60);
                    return m > 0 ? `${m}:${s.toString().padStart(2, '0')}` : t('common.time.seconds', { count: s });
                };

                return (
                    <div className="completion-stats-card">
                        <div className="completion-stats-row">
                            {stars !== null && (
                                <span className="completion-stat">
                                    {'★'.repeat(stars)}{'☆'.repeat(3 - stars)}
                                </span>
                            )}
                            <span className="completion-stat">
                                <i className="fa fa-shoe-prints"></i> {t('common.count.steps', { count: steps })}
                                {minS && steps !== minS && <span className="stat-detail"> {t('play.stats.optimal', { count: minS })}</span>}
                                {minS && steps === minS && <span className="stat-perfect"> {t('play.stats.perfect')}</span>}
                            </span>
                            {elapsed != null && (
                                <span className="completion-stat">
                                    <i className="fa fa-clock"></i> {formatTime(elapsed)}
                                </span>
                            )}
                            {hints != null && hints > 0 && (
                                <span className="completion-stat">
                                    <i className="fa fa-lightbulb"></i> {t('common.count.hints', { count: hints })}
                                </span>
                            )}
                        </div>
                        {(isHistView || isRankedCompleted) && (
                            <button className="completion-retry-btn" onClick={isHistView ? handleRetryFromHistory : handleRetry}>
                                <i className="fa fa-redo"></i> {t('play.stats.retry')}
                            </button>
                        )}
                    </div>
                );
            })()}

            {/* Navigation arrows */}
            {(() => {
                const canBack = currentHistoryGame
                    ? (historyIndex < gameHistory.length - 1 || historyHasMore)
                    : (mode === 'ranked' || mode === 'challenge'
                        ? navigation.canGoBack
                        : hasPastGames);
                // Forward arrow: for ranked/challenge, only when there's a next game to
                // advance to. navigation.canGoNext is false on the last game, so the arrow
                // is correctly disabled once the ladder is complete (instead of being a
                // clickable no-op). Practice/expert fetch a fresh game once the current
                // one is solved.
                const canForward = currentHistoryGame
                    ? true
                    : (mode === 'ranked' || mode === 'challenge')
                        ? navigation.canGoNext
                        : gameProgress.isCompleted;
                return (
                    <div className="game-nav-arrows">
                        <button
                            className={`game-nav-arrow${!canBack ? ' game-nav-disabled' : ''}`}
                            onClick={canBack ? handleNavigateBack : undefined}
                            disabled={!canBack}
                            aria-label={t('play.nav.previous')}
                        >
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </button>
                        <button
                            type="button"
                            className={`game-nav-toggle${preferences.showDistanceIndicator !== false ? ' active' : ''}`}
                            onClick={() => updatePreference('showDistanceIndicator', !(preferences.showDistanceIndicator !== false))}
                            aria-pressed={preferences.showDistanceIndicator !== false}
                            aria-label={preferences.showDistanceIndicator !== false ? t('play.nav.hideDistanceAria') : t('play.nav.showDistanceAria')}
                            title={preferences.showDistanceIndicator !== false
                                ? t('play.nav.hideDistanceTitle')
                                : t('play.nav.showDistanceTitle')}
                        >
                            <FontAwesomeIcon icon={faBullseye} />
                            <span className="game-nav-toggle-label">
                                {t(preferences.showDistanceIndicator !== false ? 'play.nav.distanceOn' : 'play.nav.distanceOff')}
                            </span>
                        </button>
                        <button
                            className={`game-nav-arrow${!canForward ? ' game-nav-disabled' : ''}`}
                            onClick={canForward ? handleNavigateForward : undefined}
                            disabled={!canForward}
                            aria-label={t('play.nav.next')}
                        >
                            <FontAwesomeIcon icon={faArrowRight} />
                        </button>
                    </div>
                );
            })()}

            {/* Rank Up Celebration */}
            {showRankUpCelebration && rankProgressUpdate?.rankChanged && (
                <RankUpCelebration
                    rankProgressUpdate={rankProgressUpdate}
                    onClose={() => setShowRankUpCelebration(false)}
                />
            )}

            {/* Wrong-Way Popup (shown when a guess jumps ≥2 steps farther from target) */}
            <WrongWayModal
                show={showWrongWayModal}
                onClose={() => setShowWrongWayModal(false)}
            />

            {/* Final Level Celebration - Mode-Specific */}
            {navigation.ladderComplete && !ladderCelebrationDismissed && (
                <div className="celebration">
                    <button
                        className="celebration-dismiss"
                        onClick={() => setLadderCelebrationDismissed(true)}
                        aria-label={t('common.close')}
                    >
                        <i className="fa fa-times"></i>
                    </button>
                    {mode === 'challenge' && challengeData ? (
                        <>
                            <div className="achievement-badge achievement-challenge celebration-badge">
                                <i className={`fa fa-${challengeData.icon}`}></i> {challengeText('name')}
                            </div>
                            <h2>{t('play.celebration.challengeTitle')}</h2>
                            <p>{t.rich('play.celebration.challengeText', { b: <strong /> }, { name: challengeText('name') })}</p>
                            <p className="challenge-stats">
                                {t('play.celebration.challengeGames', { done: gameProgress.wonGames, total: challengeData.totalGames })}
                            </p>
                            {!isAuthenticated && (
                                <button
                                    className="signup-button"
                                    onClick={() => promptLogin(t('play.signupPrompt.challenge'), { signup: true })}
                                >
                                    {t('play.celebration.signUp')}
                                </button>
                            )}
                            <p className="celebration-next-title">{t('play.celebration.whatsNext')}</p>
                            {renderNextModes('celebration_challenge')}
                            {renderAppNudge('celebration_challenge')}
                        </>
                    ) : (
                        <>
                            <div className="achievement-badge achievement-marathon celebration-badge">
                                <i className="fa fa-flag-checkered"></i> {t('play.celebration.marathonBadge')}
                            </div>
                            <h2>{t('play.celebration.ladderTitle')}</h2>
                            <p>{t('play.celebration.ladderText')}</p>
                            {ladderFeedback && (
                                <FeedbackPrompt compact context="ladder_complete" mode="ranked" lang={language}
                                    title={t('play.celebration.feedbackTitle')}
                                    gamesCompleted={parseInt(localStorage.getItem('wl_wins_total') || '0', 10) || null}
                                    onDone={() => setLadderFeedback(false)} />
                            )}
                            {!isAuthenticated && (
                                <button
                                    className="signup-button"
                                    onClick={() => promptLogin(t('play.signupPrompt.ladder'), { signup: true })}
                                >
                                    {t('play.celebration.signUp')}
                                </button>
                            )}
                            <p className="celebration-next-title">{t('play.celebration.whatsNext')}</p>
                            {renderNextModes('celebration_ranked')}
                            {renderAppNudge('celebration_ranked')}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default GamePage;
